import { chosenPlanOutOfDate, markRecordOutOfDate, NOT_FOUND_ON_RETEST, profileFingerprint, retestChosenRecord } from '../../utils/chosenPlanRefresh.ts';
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { capitalReturnedWords, capitalAccessPeriodFor } from '../../components/strategy/annuityWords.ts';
import { withChosenPlan } from '../../strategy/chosenPlan.ts';
import { householdWords, householdVoice } from '../../utils/householdWords.ts';
import RvStep1 from './RvStep1.tsx';
import { RV_COST_TOOLTIPS } from './rvCopy.ts';
import { pickCashflowCoveredLeader, cashflowCoveredNote, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, GOAL_CHIP_LABELS, friendlyGoalLabel, coveredIsBestEstate, SAME_PLAN_NOTE } from '../../strategy/cashflowCoveredGoal.ts';
import { belowBaselineWords, secondPlanOf, leadPlanOf, applyPlanChoiceWords, isEstateChip } from '../../strategy/engineGoalCards.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { MODULE_ICONS } from '../../components/icons/moduleIcons.ts';
import { ChevronLeft, ArrowRight, Banknote, DollarSign, Home, Building, Wallet, Gift, LogOut, ShieldCheck, TrendingUp as TrendingUpIcon, CheckCircle2, ChevronDown, Scale, Heart, AlertTriangle, Users, Sparkles, FileSignature, Clock, Repeat, type LucideIcon } from 'lucide-react';
import { Card, GradientText, CurrencyInput, InfoIcon, FeeExplanationAccordion, AllocationInput, Button, IconTile } from '../../components/ui/index.tsx';
import { buildRetirementVillageSuggestions, generateRetirementVillageCandidates } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import { mergeGoalLeaderStrategies, buildRecommendedOptimalStrategies } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { candidateReadings, allPlansOpenLine, SECOND_PLAN_CHIP } from '../../strategy/planChoiceRule.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { useCalcTracker } from '../../utils/calcTracker.ts';
import {
    getVillageCostSummary,
    computeInvestmentPropertyNetForAmount,
    computeRvEntryFeeFundingTotals,
    buildSimulatedFactFindForVillageStrategy,
    buildVillageMeansAssessmentOptions,
} from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import RVEntryFeeFunding, { resolveRvEntryFeeFunding, isAdviserAllocatedEntryFee } from './RVEntryFeeFunding.tsx';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock, runwayCardLine } from '../agedCare/moneyStory.tsx';
import { FundingRunway } from '../../components/narrative/index.tsx';
import RVOtherStrategiesSection from './RVOtherStrategiesSection.tsx';
import type { AnnuityDetails } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import { buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { INVESTMENT_PROFILES, newMoneyProfileOf, hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import { PlanPiecesCard } from '../../components/narrative/PlanPieces.tsx';
import { rvPlanPieces } from './rvPlanPieces.ts';
import { rvEngineSetupFromGoals, type RvEngineSetup } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import { EXTRA_ALLOWABLE_AMOUNT, FUNERAL_BOND_LIMIT, DEFAULT_INFLATION_ASSUMPTION_PCT, RETIREMENT_VILLAGE_STATES, getRetirementVillageStateRules } from '../../constants/index.ts';
import RvCauseEffect, { rvInvestmentIncomeOf } from './RvCauseEffect.tsx';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { RentedFormerHomeCgtNote, InvestmentPropertyEntryFeeNote } from './RvFieldNotes.tsx';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import { NewMoneyProfileField } from '../../components/strategy/NewMoneyProfileField.tsx';
import RvStrategyCard, { annuityOfStrategy, annuityHorizonWarningOf, rvRiskSpecificsOf } from './RvStrategyCard.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId } from '../../strategy/strategyRisks.ts';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';
import { alternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { buildRetirementVillageSoaSnapshot } from './rvSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import RvStep2 from './RvStep2.tsx';
import RvQuestions, { rvAnswersToGoals } from './RvQuestions.tsx';
import RvPlansView from './RvPlansView.tsx';
import RvHub from './RvHub.tsx';
import RvBuildPlan, { type RvSaleDetails } from './RvBuildPlan.tsx';
import { rvGuaranteedIncomeOf, rvChoiceHeld, rvCompareFor, rvFamilyRecordOf } from './rvGuaranteedIncome.ts';
import { IncomeOnlyBeforeChoose, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import { quotedIncomeFor, annuityQuoteShapeOf, annuityQuoteNote, type AnnuityQuote } from '../../strategy/annuityQuote.ts';
import { payableDebtsOf } from '../agedCare/DebtPayoffStrategy.tsx';
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import { rentAfterVacancy, DEFAULT_RV_VACANCY_WEEKS } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import { chosenRvPlanOf, rvChosenEngineIdOf, rvUnitPlansOf, rvUnitKey, withChosenRvPlan, RV_START_FRESH_KEY, RV_ANOTHER_UNIT_KEY, type RvPlanCardRecord } from './rvChosenPlan.ts';
import { rvPlanWords } from './rvPlanWords.ts';
import RvVillageCostsTile, { exitAfter } from './RvVillageCostsTile.tsx';
import { RvInBrief, type RvInBriefAnnuity } from './rvInBrief.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import RVInvestmentPropertyAllocator from './RVInvestmentPropertyAllocator.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import type { DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import type { FyBlendView } from './RVProjectionsModal.tsx';
import type { EvaluatedStrategy, RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { FinancialPosition, BreakdownRow } from '../agedCare/FinancialImpactPanel.tsx';
import { notify } from '../../components/ui/notices.tsx';
import DebtsInPlanNote, { clearableDebtTotal } from '../../components/strategy/DebtsInPlanNote.tsx';
import RentedHomeCgtNote from '../../components/strategy/RentedHomeCgtNote.tsx';
import HowItIsInvestedNote from '../../components/strategy/HowItIsInvestedNote.tsx';
import { plansInvest } from '../../utils/investedWhere.ts';

type DraftAsset = FactFindAsset;

/** One year of retirement-village projection output (calculationApi.runRetirementVillageProjections /
 *  evaluateRetirementVillageCandidates) — a free-form calc-engine result blob (see calculationApi.ts's
 *  own doc comment), typed here only for the fields this page actually reads. */
export interface RvProjectionRow {
    year?: number;
    netWorth?: number;
    netCashflow?: number;
    exitProceeds?: number;
    financials?: FinancialPosition;
    factFind?: DraftFactFind;
    assets?: Record<string, DraftAsset[] | undefined>;
    drawdownBreakdown?: BreakdownRow[];
    fyFullYearView?: FyBlendView;
    [key: string]: unknown;
}

/** A goal-leader strategy as merged/ranked by mergeGoalLeaderStrategies for the RV suggestion
 *  engine — EvaluatedStrategy plus the RV-specific fields this page reads off it. */
export interface RvEvaluatedStrategy extends EvaluatedStrategy {
    id?: string;
    projections?: RvProjectionRow[];
    inputs?: { fundFrom?: string; rentHome?: boolean; [key: string]: unknown };
    annuityIncome?: number;
    fundingBreakdown?: { label: string; amount: number }[];
    description?: string;
}

/** rvInsightFinancials useMemo result (RetirementVillageAnalyser) — passed to RetirementVillageInsightPanel. */
export interface RvInsightFinancials {
    agePensionBefore: number;
    agePensionAfter: number;
    agePensionWhy: string;
    cashflowBefore: number;
    cashflowAfter: number;
    villageOngoingAnnual: number;
    legacyEstimate: number;
    noVillageScenario: boolean;
    cashflowDeficitNarrative: string | null;
    estateTimingNote: string;
    rentAssistance: number;
}

/** investmentPropertySaleSummary useMemo result. */
export interface InvestmentPropertySaleSummary {
    netProceeds: number;
    estimatedCGT: number;
}

/** getVillageCostSummary(villageInputs) result. */
export interface VillageCostSummary {
    entryFee: number;
    annualOngoing: number;
    exitDmf: number;
    exitNetToResident: number;
    [key: string]: unknown;
}

/** Village cost/contract inputs (villageInputs state) — see getVillageCostSummary in
 *  shared/src/calculation/retirementVillageCalculations.ts for the shape these feed into.
 *  A `type` (not `interface`) so it structurally satisfies calculationApi.ts's
 *  `Record<string, unknown>`-typed ephemeral strategy-bag parameters without a cast at every call. */
export type VillageInputs = {
    entryFee: number;
    ongoingWeeklyOrMonthly: number;
    ongoingFrequency: 'weekly' | 'monthly';
    dmfPctPerYear: number;
    dmfCapYears: number;
    yearsResidency: number;
    growthRatePct: number;
    capitalGainShareToResident: number;
    reinstatementCap: number;
};

/** RVSellHomeAllocator's editable state (Sell/downsize the family home strategy). */
export type SellHomeDetails = {
    /** Whose name a bought investment property is in; a couple's default is both. */
    investmentPropertyOwner?: string;
    ipLoanAmount?: number;
    ipLoanRate?: number;
    ipLoanTermYears?: number;
    salePrice?: number;
    sellingCosts?: number;
    downsizerContribution?: number;
    partnerDownsizerContribution?: number;
    commenceIncomeStream?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
    purchaseAnnuity?: number;
    rvAnnuityType?: 'lifetime' | 'fixedTerm' | 'guaranteed';
    rvAnnuityResidualPercent?: number;
    rvAnnuityTermYears?: number;
    rvAnnuityIndexation?: boolean;
    rvAnnuityReversionary?: boolean;
    rvAnnuityOwner?: 'client' | 'partner' | 'joint';
    rvAnnuityIncomeOverride?: number;
    purchaseInvestmentProperty?: number;
    investmentPropertyRent?: number;
    investmentPropertyExpenses?: number;
    /** The state the bought property is in and the duty on buying it — struck by StampDutyFields, overwritable. */
    investmentPropertyState?: string;
    investmentPropertyStampDuty?: number;
    purchaseShares?: number;
};

/** Rent-the-family-home strategy state. */
export type RentDetails = {
    rentAmount: number;
    rentExpenses: number;
    partnerRent: number;
};

/** RVInvestmentPropertyAllocator's editable state (Sell investment property strategy). */
export type RvInvestmentPropertyDetails = {
    selectedPropertyId?: number | string | null;
    salePrice?: number;
    sellingCosts?: number;
    costBase?: number;
    entryFeeAllocation?: number;
    downsizerContribution?: number;
    partnerDownsizerContribution?: number;
    commenceIncomeStream?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
};

/** Gifting / funeral bond / annuity "other strategies" (RVOtherStrategiesSection). */
export type OtherStrategies = {
    gifting: boolean;
    giftingAmount: number;
    giftingSource: string;
    funeralBonds: boolean;
    funeralBondAmount: number;
    funeralBondSource: string;
    annuity: boolean;
};

export type RvOverallStrategy = {
    rentHome: boolean;
    downsizeHome: boolean;
    sellInvestmentProperty: boolean;
};

/** A saved strategy card (Save Strategy panel / Compare modal). Shared shape across the app's
 *  strategy features (aged care, SAH, RV) — inputs/outputs are strategy-specific free-form bags. */
export interface SavedStrategy {
    id: string;
    name: string;
    type: string;
    inputs: Record<string, unknown>;
    outputs: Record<string, unknown>;
    createdAt?: string;
    savedAt?: string;
}

const RV_EXPLAIN_SECTIONS = [
  { id: 'entry', icon: DollarSign, title: 'Entry payment', summary: 'A one-off amount when you move in—often called the ingoing contribution or purchase price. Many villages set this out in a disclosure statement.', details: <p className="text-gray-700 leading-relaxed">The entry payment is your upfront cost to secure your unit. It can be a large sum and may be close to market value for long-term lease or purchase arrangements. Additional one-off fees (e.g. garage, parking) may apply. Get the village&apos;s disclosure statement for exact figures.</p>, applies: null },
  { id: 'ongoing', icon: Banknote, title: 'Regular fees', summary: 'Weekly or monthly fees for services, maintenance and shared facilities. The disclosure statement usually shows how and when these can increase.', details: <p className="text-gray-700 leading-relaxed">Recurrent charges are split among residents from the village&apos;s annual budget. They typically cover management, insurance, maintenance, communal facilities (pool, gym, gardens) and 24-hour emergency. Optional services (meals, cleaning, laundry) are usually itemised and only charged if used. Contracts must state how and when fees can increase (e.g. CPI, notice periods).</p>, applies: null },
  { id: 'exit', icon: LogOut, title: 'When you leave', summary: "A departure fee (deferred management fee) is usually taken from the amount you get back. You may also get a share of any increase in the unit's value.", details: <p className="text-gray-700 leading-relaxed">The departure fee (DMF) is a major cost and is contract-specific—it must be in the disclosure statement. It is often a percentage per year of residency with a cap (e.g. 2% per year, capped at 20% after 10 years). The percentage can apply to your entry payment or to the amount the next resident pays. Other exit costs can include reinstatement (returning the unit to original condition) and refurbishment. Your village&apos;s disclosure statement will spell out the exact rules.</p>, applies: null },
  { id: 'contract-right', icon: Home, title: 'Right to live there (no ownership)', summary: "You pay an entry amount and regular fees but don't own the unit. When you leave, you get back an amount (often when the unit is re-sold) after the departure fee and other deductions.", details: <div className="text-gray-700 leading-relaxed space-y-2"><p>This is the most common arrangement — legally a <strong>loan or licence</strong>: your entry payment buys a contractual right to occupy under your state&apos;s Retirement Villages Act, while the operator keeps title. Practical consequences worth knowing:</p><ul className="list-disc pl-5 space-y-1"><li>You can&apos;t borrow against the unit or sell it yourself — your money comes back through the contract, not a property sale.</li><li>Your exit entitlement is usually paid when the unit is <strong>re-licensed to the next resident</strong>, so timing depends on how quickly the village re-sells. Some states guarantee payment after a set period — see the state protections further down this page.</li><li>Questions to ask before signing: who pays the regular fees between your leaving and the re-sale? Who sets the re-sale price and appoints the agent? What happens to your entitlement if the village is sold or the operator fails?</li></ul></div>, applies: null },
  { id: 'contract-own', icon: Building, title: 'You own the unit', summary: 'For example strata or community title. You own the unit (sometimes with rules on resale). When you leave, you sell it or the village may buy it back; the departure fee and contract terms still apply.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p>With strata or community title you hold real title — which changes the mechanics, not the economics:</p><ul className="list-disc pl-5 space-y-1"><li>You may pay <strong>two sets of ongoing charges</strong>: strata levies for the building AND village fees for services — check what each covers.</li><li>Resale is often restricted to age-qualified buyers, which can slow the sale or soften the price compared with the open market; some contracts give the operator a buy-back right at a formula price.</li><li>Because you hold title, stamp duty usually applies on the way in — a real cost the loan/licence route avoids.</li><li>The departure fee still applies on exit in most ownership contracts — ownership does not escape the DMF.</li></ul></div>, applies: null },
  { id: 'pension', icon: Wallet, title: 'Age Pension impact', summary: `Services Australia uses your entry payment to decide homeowner status (threshold $${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}). Above = homeowner (entry not in assets); at or below = non-homeowner (entry counted, Rent Assistance possible).`, details: <div className="text-gray-700 leading-relaxed space-y-2"><p>Why the classification matters more than it sounds — a worked example:</p><ul className="list-disc pl-5 space-y-1"><li>Entry payment $600,000, threshold ${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}: you&apos;re a <strong>homeowner</strong> — the $600,000 vanishes from the assets test entirely, but your remaining assets are then measured against the <em>lower</em> homeowner thresholds.</li><li>Entry payment $200,000: you&apos;re a <strong>non-homeowner</strong> — the $200,000 IS counted, but you get the <em>higher</em> non-homeowner thresholds and may qualify for <strong>Rent Assistance</strong> on your regular fees, which can be worth thousands a year.</li><li>Money left over after the entry payment (say, from selling your home) becomes a financial asset — deemed for the income test and counted for the assets test. That&apos;s often the biggest pension effect of the whole move, and it&apos;s why the calculator models the entry payment and the leftover together rather than in isolation.</li></ul></div>, applies: null },
  { id: 'legacy', icon: Gift, title: 'What you leave to family', summary: 'The amount you get back when you leave (after the departure fee and other costs) is often paid when the unit is sold. That amount affects what you can leave to your family.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p>Three things families are most often surprised by:</p><ul className="list-disc pl-5 space-y-1"><li><strong>The wait.</strong> The exit entitlement is a contract debt owed by the operator, not a property your estate can sell — it&apos;s typically paid only when the unit re-sells, which can take months (some state Acts set a backstop period — see the state section below). Until then it may be earning nothing while regular fees, in some states, continue.</li><li><strong>The departure fee grows with time.</strong> A 3%-per-year DMF capped at 10 years means an estate loses far more after a 10-year stay than after a 3-year one — the &quot;You get back (est.)&quot; figure in the calculator shows exactly this for your inputs.</li><li><strong>The gain share cuts both ways.</strong> If the contract gives you 50% of any capital gain, the other 50% never reaches your estate — and some contracts pass the resident a share of a capital <em>loss</em> too.</li></ul></div>, applies: null }
];


/**
 * The one help device on a form field (owner, 22 and 24 Sep 2026): a dotted "what's this?" that
 * opens a small card over the page — never an icon, and never an in-place expansion, which pushed
 * this two-column form apart. The shared InfoIcon; the aged care analyser uses the same one.
 */
const WhatsThis = ({ text }: { text: string }) => <InfoIcon text={text} />;

/** The contract as kept on the file (goals.rv*), or null when step 1 has never been filled in. */
function villageInputsFromGoals(factFind: DraftFactFind | null | undefined): Partial<VillageInputs> | null {
  const g = (factFind?.goals || {}) as Record<string, unknown>;
  if (g.rvEntryFee == null) return null;
  const num = (x: unknown, d: number) => (Number.isFinite(Number(x)) ? Number(x) : d);
  return {
    entryFee: num(g.rvEntryFee, 0),
    ongoingWeeklyOrMonthly: num(g.rvFees, 0),
    ongoingFrequency: g.rvFeesFrequency === 'weekly' ? 'weekly' : 'monthly',
    dmfPctPerYear: num(g.rvDmfPct, 0),
    dmfCapYears: num(g.rvDmfCapYears, 0),
    yearsResidency: num(g.rvYearsResidency, 0),
    growthRatePct: num(g.rvGrowthPct, 3),
    capitalGainShareToResident: num(g.rvGainSharePct, 50) / 100,
    reinstatementCap: num(g.rvReinstatementCap, 0),
  };
}
function villageInputsToGoals(v: VillageInputs): Record<string, unknown> {
  return {
    rvEntryFee: v.entryFee || 0,
    rvFees: v.ongoingWeeklyOrMonthly || 0,
    rvFeesFrequency: v.ongoingFrequency,
    rvDmfPct: v.dmfPctPerYear || 0,
    rvDmfCapYears: v.dmfCapYears || 0,
    rvYearsResidency: v.yearsResidency || 0,
    rvGrowthPct: v.growthRatePct ?? 3,
    rvGainSharePct: Math.round((v.capitalGainShareToResident ?? 0.5) * 1000) / 10,
    rvReinstatementCap: v.reinstatementCap || 0,
  };
}

/** Numeric-input strings (mid-edit draft values, incl. '') coerced to a real number — the same
 *  defensive `parseFloat(String(x))` idiom used at calc-engine read sites (see
 *  shared/src/calculation/factFindTypes.ts). */
const n = (v: unknown): number => parseFloat(String(v ?? '')) || 0;

interface RetirementVillageExplainedProps {
  setActivePage: (page: string) => void;
  onContinue: () => void;
  /** Set when opened from inside the module: both buttons say it and go back there. */
  backLabel?: string;
  rvState: string;
  setRvState: (state: string) => void;
}

/** "Is a village right for you?" — each point leads with the claim, then the detail. */
const RV_CHECKLIST_LOVES: { icon: LucideIcon; lead: string; body: React.ReactNode }[] = [
  { icon: Users, lead: 'Community on your doorstep.', body: "Neighbours at a similar stage of life, clubs and activities, and someone to notice if you don't show up for coffee." },
  { icon: Home, lead: 'A secure, low-maintenance home.', body: 'No gutters, no lawns, and help on call if you want it.' },
  { icon: Sparkles, lead: 'Money and energy freed up.', body: 'Moving out of a large family home can release both for the things you actually want to do.' },
];
const RV_CHECKLIST_WEIGH_UP: { icon: LucideIcon; lead: string; body: React.ReactNode }[] = [
  { icon: FileSignature, lead: "You're buying a contract, not just a home.", body: 'Leaving costs real money (the departure fee below), so a village suits people planning to stay.' },
  { icon: Clock, lead: 'Your exit money usually waits for a re-sale.', body: "That timing matters for your estate, and for funding aged care if it's ever needed." },
  { icon: Repeat, lead: 'A later move into aged care means moving twice.', body: 'Worth modelling both steps, which this tool can do.' },
  { icon: Scale, lead: 'Compare contracts, not brochures.', body: "Look at two or three villages' contracts and get legal advice before signing. Your state's protections are summarised further down this page." },
];

const RetirementVillageExplained = ({ setActivePage, onContinue, rvState, setRvState, backLabel }: RetirementVillageExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggleAccordion = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  const stateRules = getRetirementVillageStateRules(rvState);
  return (
    <div className="max-w-6xl mx-auto">
      {/* Opened from inside the module, the guide goes back to where the reader was, top and bottom, as aged care's
          does (owner, 22 Oct 2026: "Back to Dashboard" at the top left the module; the way back was at the foot). */}
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => (backLabel ? onContinue() : setActivePage('Dashboard'))} className="mb-4 -ml-2" data-testid="rv-guide-back">{backLabel || 'Back to Dashboard'}</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={MODULE_ICONS.retirement_village} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Retirement Village (Independent Living)</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Plenty of Australians choose village living for the lifestyle: your own home without the maintenance, a ready-made community, and facilities — gardens, pools, social clubs — a short walk away. You live independently, add services like cleaning or meals only if and when you want them, and lock up and travel whenever you like. It&apos;s not aged care, and no government assessment is needed — it&apos;s a lifestyle choice with a financial contract attached, and this section helps you weigh both.</p>
        <p className="text-sm text-slate-500">Many villages provide a <strong>disclosure statement</strong> that sets out costs and contract terms. If you have or expect one, use it when modelling your options in the calculator.</p>
      </div>

      {/* The honest-decision framing lives HERE, before the fee mechanics: lifestyle upsides and the
          things worth weighing, without duplicating the cost accordion below. Collapsed by default.
          Owner, 1 Oct 2026: "it just looks bland" — each point now leads with a short bold claim and
          its own icon, in two titled panels (what residents love / what to weigh up). */}
      <details className="mb-8 rounded-2xl border border-slate-200 bg-white shadow-card group overflow-hidden">
        <summary className="cursor-pointer list-none p-5 flex items-center gap-4 [&::-webkit-details-marker]:hidden">
          <IconTile icon={Scale} tone="brand" variant="soft" size="md" className="hidden sm:inline-flex" />
          <div className="flex-1 min-w-0">
            <span className="block text-lg font-bold text-gray-900">Is a village right for you?</span>
            <span className="block text-sm text-slate-600">The honest checklist — what residents love, and what to weigh up before you sign.</span>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-sm font-semibold text-blue-700">
            <span className="group-open:hidden">Show</span>
            <span className="hidden group-open:inline">Hide</span>
            <ChevronDown size={16} className="transition-transform group-open:rotate-180" aria-hidden />
          </span>
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 pb-5">
          <section className="rounded-xl border border-emerald-200 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-emerald-800">
              <Heart size={16} aria-hidden /> What residents tend to love
            </h3>
            <ul className="space-y-3.5">
              {RV_CHECKLIST_LOVES.map(({ icon: Icon, lead, body }) => (
                <li key={lead} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-navy text-white">
                    <Icon size={16} aria-hidden />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{lead}</span> {body}
                  </p>
                </li>
              ))}
            </ul>
          </section>
          <section className="rounded-xl border border-amber-200 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-800">
              <AlertTriangle size={16} aria-hidden /> What to weigh up before signing
            </h3>
            <ul className="space-y-3.5">
              {RV_CHECKLIST_WEIGH_UP.map(({ icon: Icon, lead, body }) => (
                <li key={lead} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
                    <Icon size={16} aria-hidden />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{lead}</span> {body}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </details>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">Your options and what they cost</h2>
      <p className="text-base text-gray-600 mb-4">Tap each one to see how it works — the upfront entry payment, the regular fees, what happens when you leave, who owns the unit, and how it affects your Age Pension and estate.</p>
      <div className="space-y-4 mb-8">
        {RV_EXPLAIN_SECTIONS.map(fee => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggleAccordion(fee.id)} factFind={{}} />
        ))}
      </div>

      {/* State / territory consumer-protection. The money side (Age Pension, Rent Assistance, deeming) is
          federal and identical nationwide; what differs by state is the Retirement Villages Act — especially
          how long you or your estate may wait to be repaid after leaving, and ongoing fees after vacating. */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <IconTile icon={ShieldCheck} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">The rules where you live</h2>
              <p className="text-base text-slate-600 max-w-xl">The money side (Age Pension, Rent Assistance) is the same Australia-wide. What changes by state is the consumer-protection law — most importantly, how long you (or your estate) might wait to be repaid after you leave, and how long you keep paying fees after moving out.</p>
            </div>
          </div>
          <div className="shrink-0">
            <label htmlFor="rv-state" className="block text-xs font-medium text-slate-600 mb-1">Your state or territory</label>
            <select
              id="rv-state"
              value={rvState || ''}
              onChange={(e) => setRvState && setRvState(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[14rem]"
            >
              <option value="">Select…</option>
              {RETIREMENT_VILLAGE_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900">Getting your money back when you leave</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${stateRules.buyback.hasStatutoryTimeframe ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {stateRules.buyback.hasStatutoryTimeframe ? 'Statutory timeframe' : 'Contract-based'}
              </span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{stateRules.buyback.summary}</p>
          </div>
          <div className="rounded-lg border border-slate-200 border-l-4 border-l-slate-400 bg-slate-50 p-3">
            <span className="block text-sm font-semibold text-gray-900 mb-1">Ongoing fees after you move out</span>
            <p className="text-sm text-slate-700 leading-relaxed">{stateRules.recurrentChargesAfterVacating}</p>
          </div>
          {stateRules.note && <p className="text-xs text-slate-500 leading-relaxed">{stateRules.note}</p>}
        </div>

        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          {stateRules.code ? <>Governed by the <strong>{stateRules.act}</strong>. </> : <>Select your state above for the rules that apply to you. </>}
          See <a href={stateRules.regulatorUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{stateRules.regulator}</a>. Your village&apos;s {(stateRules.disclosureDocument || 'disclosure statement').toLowerCase()} and contract always govern.
        </p>
      </div>

      <p className="text-xs text-slate-500 mb-6">This tool is for illustration only. Get the village&apos;s disclosure statement and independent legal and financial advice. Consumer-protection rules differ by state and territory—see &quot;Rules where you live&quot; above for the law and regulator that apply to you.</p>
      <div className="text-center">
        <Button onClick={onContinue} variant="brand" size="lg" iconRight={ArrowRight}>
          {backLabel || 'Continue to calculator'}
        </Button>
      </div>
    </div>
  );
};

type FundingMap = Record<string, number>;
type StrategyMode = 'manual' | 'optimised' | '';
type FundFrom = 'investment_property' | 'sell_home' | 'liquid';

interface RetirementVillageAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  onBack: () => void;
  savedStrategies: SavedStrategy[];
  setSavedStrategies: React.Dispatch<React.SetStateAction<SavedStrategy[]>>;
  rvState: string;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

/** An empty contract: what step 1 opens on for a new model (growth 3% and half the capital gain are the starting estimates). */
const RV_FRESH_VILLAGE_INPUTS: VillageInputs = { entryFee: 0, ongoingWeeklyOrMonthly: 0, ongoingFrequency: 'monthly', dmfPctPerYear: 0, dmfCapYears: 0, yearsResidency: 0, growthRatePct: 3, capitalGainShareToResident: 0.5, reinstatementCap: 0 };
/** The village and the step 3 answers a new model takes off the file, so nothing carries over. */
const RV_FRESH_GOAL_KEYS = ['rvVillageName', 'rvHomeRoute', 'rvRentPerWeek', 'rvRentCosts', 'rvVacancyWeeks', 'rvEmergencyReserve', 'rvResidualUses', 'rvEntryDate'];

const RetirementVillageAnalyser = ({ setActivePage, factFind, onBack, savedStrategies, setSavedStrategies, rvState, setFactFind, onSoaGenerated }: RetirementVillageAnalyserProps) => {
  /*
   * HOW THE MODULE OPENS (rebuild M2, 21 Oct 2026). With a plan chosen, on the plan page (RvHub). From Home's
   * "Start a new model", on an empty step 1 with step 3's questions asked again. From "Model another village
   * or unit", on step 1 with everything carried over. The session flags are read here and cleared in an
   * effect below, so a second render (StrictMode) reads the same answer.
   */
  const [openedFresh] = useState<boolean>(() => { try { return !!sessionStorage.getItem(RV_START_FRESH_KEY); } catch { return false; } });
  const [anotherUnit, setAnotherUnit] = useState<boolean>(() => { try { return !!sessionStorage.getItem(RV_ANOTHER_UNIT_KEY); } catch { return false; } });
  const [rvView, setRvView] = useState<'hub' | 'steps'>(() => (!openedFresh && !anotherUnit && chosenRvPlanOf(savedStrategies as never) ? 'hub' : 'steps'));
  /** What the plan page asked for, done once the tested plans are back. */
  const [pendingHubAction, setPendingHubAction] = useState<'explain' | 'advice' | null>(null);
  // Unit growth 3%/yr is a conservative starting estimate the adviser can overwrite. Years in
  // village deliberately stays 0 ("not modelling an exit"): the projection engine CLAMPS a stay
  // longer than the projection window to its final year (resolveExitYear in the village plugin,
  // "preserved rather than endorsed") — a defaulted 10-year stay in a 5-year projection modelled
  // every resident LEAVING in year 5, entitlement and all, inflating every mover's legacy by ~the
  // exit entitlement and poisoning the suggestion-engine rankings. Until the clamp's semantics are
  // fixed engine-side, an exit is modelled only when the adviser explicitly enters a residency.
  // Opens on the contract last worked on — the chosen village plan's, else the newest saved one's
  // (village review, 18 Oct 2026: returning to the module reset every contract figure to $0).
  const [villageInputs, setVillageInputs] = useState<VillageInputs>(() => {
    const defaults: VillageInputs = { ...RV_FRESH_VILLAGE_INPUTS };
    // On the file first (village rebuild, 21 Oct 2026: step 1's answers are goals.rv*), then the chosen plan's.
    if (openedFresh) return defaults;
    const fromGoals = villageInputsFromGoals(factFind);
    if (fromGoals) return { ...defaults, ...fromGoals };
    const plans = (savedStrategies || []).filter((s) => s.type === 'retirement_village' && (s.inputs as { villageInputs?: unknown } | undefined)?.villageInputs);
    const from = plans.find((s) => (s as { chosen?: boolean }).chosen === true) ?? plans[plans.length - 1];
    const saved = (from?.inputs as { villageInputs?: Partial<VillageInputs> } | undefined)?.villageInputs;
    return saved ? { ...defaults, ...saved } : defaults;
  });
  // Every change to the contract is written to the file, so it is saved by the autosave.
  useEffect(() => {
    if (!setFactFind) return;
    setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...villageInputsToGoals(villageInputs) } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villageInputs]);
  const [strategyName, setStrategyName] = useState('');
  // '' until the user chooses — the mode choice is the FIRST act, matching the aged care and SAH
  // modules (owner-caught here too, 2026-08-25: Manual Control defaulting to on skipped the choice).
  // Restoring a saved strategy still lands in manual (see the restore handler).
  const [strategyMode, setStrategyMode] = useState<StrategyMode>('');
  /*
   * THE THREE STEPS (owner, 24 Sep 2026: "replicate the UI and functionality as much as possible
   * as to what you did with the aged care suggestion engine"). 1: the village contract and the
   * move-in date. 2: where the client stands STAYING PUT — the one baseline. 3: the two ways to fund
   * the move, with the plan summary beside them. The strip is a map, not a gate.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const rvVoice = householdVoice(factFind);
  const goToStep = (next: AnalyserStep) => {
    // Step 3 opens on the plans we tested; Manual Control is "Build a plan yourself" from there.
    if (next === 3) setStrategyMode((m) => (m === '' ? 'optimised' : m));
    setStep(next);
    scrollAppToTop();
  };
  const [activeSavedStrategyId, setActiveSavedStrategyId] = useState<string | null>(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  /** "Understanding your plan" — the full recap, opened from the plan summary. */
  const [showRvExplanation, setShowRvExplanation] = useState(false);
  const [showStrategyOptions, setShowStrategyOptions] = useState(true);
  const [showOtherStrategies, setShowOtherStrategies] = useState(false);
  const [otherStrategies, setOtherStrategies] = useState<OtherStrategies>({
    gifting: false,
    giftingAmount: 0,
    giftingSource: 'bankAccounts',
    funeralBonds: false,
    funeralBondAmount: 0,
    funeralBondSource: 'bankAccounts',
    annuity: false,
  });
  const [annuityRecord, setAnnuityRecord] = useState<AnnuityDetails | null>(null);
  const [rvOverallStrategy, setRvOverallStrategy] = useState<RvOverallStrategy>({ rentHome: false, downsizeHome: false, sellInvestmentProperty: false });
  const [rvInvestmentPropertyDetails, setRvInvestmentPropertyDetails] = useState<RvInvestmentPropertyDetails>(() => {
    const first = factFind?.assets?.investmentProperty?.[0];
    const val = first?.value || 0;
    return {
      selectedPropertyId: first?.id ?? null,
      salePrice: val,
      sellingCosts: val * 0.025,
      costBase: first?.costBase ?? 0,
      entryFeeAllocation: 0,
      downsizerContribution: 0,
      partnerDownsizerContribution: 0,
      commenceIncomeStream: false,
      giftingAmount: 0,
      funeralBondPurchase: 0
    };
  });
  const [sellHomeDetails, setSellHomeDetails] = useState<SellHomeDetails>({
    salePrice: n(factFind?.home?.value),
    sellingCosts: n(factFind?.home?.value) * 0.025,
    downsizerContribution: 0,
    partnerDownsizerContribution: 0,
    commenceIncomeStream: false,
    giftingAmount: 0,
    funeralBondPurchase: 0,
    purchaseAnnuity: 0,
    purchaseInvestmentProperty: 0,
    investmentPropertyRent: 0,
    investmentPropertyExpenses: 0,
    investmentPropertyState: rvState || '',
    investmentPropertyStampDuty: 0,
    purchaseShares: 0
  });
  const [rentDetails, setRentDetails] = useState<RentDetails>(() => ({ rentAmount: n(factFind?.home?.likelyRent), rentExpenses: n(factFind?.home?.likelyExpenses), partnerRent: 0 }));
  // Keep rent assumptions in sync with Client Profile (fact find) when user hasn't entered values yet
  useEffect(() => {
    if (!factFind?.home) return;
    const lr = n(factFind.home.likelyRent);
    const le = n(factFind.home.likelyExpenses);
    if ((lr > 0 || le > 0) && rentDetails.rentAmount === 0 && rentDetails.rentExpenses === 0) {
      setRentDetails(prev => ({ ...prev, rentAmount: lr || 0, rentExpenses: le || 0 }));
    }
  }, [factFind?.home?.likelyRent, factFind?.home?.likelyExpenses, rentDetails.rentAmount, rentDetails.rentExpenses]);
  const [entryFeeFunding, setEntryFeeFunding] = useState<FundingMap>({});
  const [entryFeeCgtOverrides, setEntryFeeCgtOverrides] = useState<Record<string, number | null>>({});
  // 10 years, the same as every module but aged care (owner, 19 Oct 2026: "10 years makes sense").
  const [localLegacyYears, setLocalLegacyYears] = useState(10);
  // Suggestion Engine state (Task #3): computed off-thread-of-render so the loader paints first.
  const [seLoading, setSeLoading] = useState(false);
  const [rvSuggestions, setRvSuggestions] = useState<{ strategies: EvaluatedStrategy[]; recommended: RecommendedStrategyEntry[]; stayHome?: EvaluatedStrategy | null } | null>(null);
  const [seDeepDive, setSeDeepDive] = useState<{ projections: RvProjectionRow[]; name: string } | null>(null);
  // "What could go wrong" for one engine card (StrategyRisksPanel).
  const [riskStrategy, setRiskStrategy] = useState<RvEvaluatedStrategy | null>(null);

  /**
   * The Statement of Advice for the selected engine strategy (owner, 2 Oct 2026). Built from the
   * figures already on screen (rvSoa.ts), opened and downloaded like aged care's, and handed to App
   * so it lands on the advice register with its document snapshot.
   */
  const generateRvSoa = (strategy: RvEvaluatedStrategy) => {
    const snapshot = buildRetirementVillageSoaSnapshot({
      strategy,
      all: (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[],
      leaders: visibleSeCards.map((c) => ({ strategy: c.strategy, badges: c.badges })),
      baseline: (rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined) ?? null,
      factFind,
      summary,
      villageInputs,
      legacyYears: Number(localLegacyYears) || 5,
    });
    openModuleSoa(snapshot);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'retirement_village', source: 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null);
  /** The card whose "Explain this" is open — explaining a plan never selects it (review, 18 Oct 2026). */
  const [explainSeId, setExplainSeId] = useState<string | null>(null); // selected SE strategy → drives the narrative + Financial Impact panels
  /*
   * THE STRATEGY ANALYSIS SETUP (owner, 24 Sep 2026). Opens once per visit when the Suggestion
   * Engine is chosen, and again from "Adjust setup". Its answers are saved on the client record
   * (goals.rvPurchaseState / rvAnnuityShareCapPct / rvCashflowTarget), so a returning adviser sees
   * their own choices rather than the defaults. While it is open the engine waits: one run on the
   * answers, not a run on the defaults and then another.
   */
  const [rvSetup, setRvSetup] = useState<RvEngineSetup>(() => rvEngineSetupFromGoals(factFind, rvState));
  const [rvSetupOpen, setRvSetupOpen] = useState(false);
  // Step 3 asks its questions before the first run, and again from "Things that change the answer" (village rebuild, 21 Oct 2026).
  const [rvAsking, setRvAsking] = useState<boolean>(() => openedFresh || (factFind?.goals as Record<string, unknown> | undefined)?.rvHomeRoute == null);
  // A fresh model: the flags cleared, and the village and the answers taken off the file so nothing carries over.
  useEffect(() => {
    try { sessionStorage.removeItem(RV_START_FRESH_KEY); sessionStorage.removeItem(RV_ANOTHER_UNIT_KEY); } catch { /* storage unavailable */ }
    if (openedFresh && setFactFind) {
      setFactFind((prev) => {
        const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
        for (const k of RV_FRESH_GOAL_KEYS) delete g[k];
        return { ...prev, goals: g };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Answered before, on this file: don't ask again on every visit (review, 18 Oct 2026). "Adjust setup" still opens it.
  const [rvSetupSeen, setRvSetupSeen] = useState(() => (factFind?.goals as Record<string, unknown> | undefined)?.rvAnnuityAppetite != null);
  const rvNetFreedAfterEntry = useMemo(() => {
    const homeValue = n(factFind?.home?.value);
    if (!(homeValue > 0)) return 0;
    return Math.max(0, homeValue - Math.round(homeValue * 0.025) - (n(villageInputs.entryFee) || 0));
  }, [factFind?.home?.value, villageInputs.entryFee]);
  // Entry fee funding: only sell IP or sell/downsize home use property; otherwise liquid (including when renting the home for cashflow).
  const fundFrom: FundFrom = rvOverallStrategy.sellInvestmentProperty ? 'investment_property' : rvOverallStrategy.downsizeHome ? 'sell_home' : 'liquid';
  const EMERGENCY_RESERVE = getRecommendedEmergencyFund(factFind); // 6 months of living expenses
  /** The money kept for emergencies: step 3's answer, else the suggestion (the greater of six months' costs and $50,000). */
  const rvReserve = Number.isFinite(Number((factFind?.goals as { rvEmergencyReserve?: unknown } | undefined)?.rvEmergencyReserve)) && (factFind?.goals as { rvEmergencyReserve?: unknown } | undefined)?.rvEmergencyReserve != null
    ? Number((factFind?.goals as { rvEmergencyReserve?: number }).rvEmergencyReserve)
    : EMERGENCY_RESERVE;
  /*
   * WHAT THE PROPERTY PAYS, AND WHAT SAVINGS MUST (owner-caught, 24 Sep 2026). On the sell-a-property
   * routes the shortfall was only ever computed for the savings route, so an $800,000 contribution
   * against $651,966 of net proceeds read "paid in full" and offered nowhere to allocate the other
   * $148,034. The property's share is what its sale nets, capped at the contribution; the rest is
   * the target the savings allocator below is asked for, and what the engine tops up in the same
   * order (shared topUpEntryFeeFromLiquid).
   */
  const propertyTowardEntry: number = useMemo(() => {
    const entryFee = villageInputs.entryFee;
    if (!(entryFee > 0)) return 0;
    if (fundFrom === 'investment_property') {
      const list = factFind?.assets?.investmentProperty || [];
      const ip = list.find((p) => String(p.id) === String(rvInvestmentPropertyDetails.selectedPropertyId)) || list[0];
      if (!ip) return 0;
      const { netProceeds } = computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, ip as unknown as FactFindAsset, n(ip.value));
      return Math.min(entryFee, Math.max(0, n(rvInvestmentPropertyDetails.entryFeeAllocation)), netProceeds);
    }
    if (fundFrom === 'sell_home') {
      return Math.min(entryFee, Math.max(0, n(sellHomeDetails.salePrice) - n(sellHomeDetails.sellingCosts)));
    }
    return 0;
  }, [fundFrom, villageInputs.entryFee, factFind, rvInvestmentPropertyDetails.selectedPropertyId, rvInvestmentPropertyDetails.entryFeeAllocation, sellHomeDetails.salePrice, sellHomeDetails.sellingCosts]);
  /** What the savings allocator is asked for: the whole contribution, or what the sale leaves. */
  const liquidTargetForEntryFee = fundFrom === 'liquid' ? villageInputs.entryFee : Math.max(0, villageInputs.entryFee - propertyTowardEntry);
  const defaultEntryFeeAllocation: FundingMap = useMemo(() => {
    const entryFee = liquidTargetForEntryFee;
    if (entryFee <= 0 || !factFind?.assets) return {};
    const order = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'];
    let remaining = entryFee;
    const out: FundingMap = {};
    order.forEach(type => {
      if (remaining <= 0 || !factFind.assets![type]) return;
      if (type === 'bankAccounts') {
        const bankAccounts = factFind.assets!.bankAccounts || [];
        const totalBank = bankAccounts.reduce((s, a) => s + n(a.value), 0);
        const availableFromBank = Math.max(0, totalBank - EMERGENCY_RESERVE);
        let takenFromBank = 0;
        bankAccounts.forEach(asset => {
          if (remaining <= 0 || takenFromBank >= availableFromBank) return;
          const assetId = `bankAccounts-${asset.id}`;
          const val = n(asset.value);
          const canTake = Math.min(val, remaining, availableFromBank - takenFromBank);
          if (canTake > 0) {
            out[assetId] = canTake;
            remaining -= canTake;
            takenFromBank += canTake;
          }
        });
        return;
      }
      (factFind.assets![type] || []).forEach((asset: DraftAsset) => {
        if (remaining <= 0) return;
        const assetId = `${type}-${asset.id}`;
        const val = n(asset.value);
        const take = Math.min(val, remaining);
        if (take > 0) { out[assetId] = take; remaining -= take; }
      });
    });
    /*
     * NO PARTIAL PRE-FILL WHEN SAVINGS CANNOT COVER IT (owner, 2026-09-01).
     *
     * `remaining > 0` here means the draw order ran out of liquid assets before it reached the entry
     * contribution — the savings genuinely cannot fund this move. Handing back the partial
     * allocation anyway put every slider at maximum, drew down the account-based pension by default,
     * and then told the client on screen that none of it would work and the real decision was
     * elsewhere. They had to read a wall of reasoning about a plan the tool already knew was
     * impossible, and then undo it.
     *
     * A village contribution has to be paid in full, so a partial allocation is not a partial plan —
     * it is no plan. Returning nothing leaves the sliders at zero and lets the screen lead with the
     * routes that CAN fund it (selling or renting the home, selling an investment property), which
     * is where the decision actually is.
     */
    if (remaining > 0) return {};
    /*
     * GROSS UP FOR THE TAX ON A SALE (village review, 18 Oct 2026: the default left $1,200 of the entry
     * payment unfunded, because shares sold at the entry amount raise that amount LESS the capital gains
     * tax on them). Sell a little more of the same holdings until what reaches the village covers it, or
     * the holdings run out — in which case the shortfall line says so, as before.
     */
    for (let pass = 0; pass < 6; pass++) {
      const net = computeRvEntryFeeFundingTotals(factFind as unknown as FactFind, out, {}).netTotal;
      const gap = entryFee - net;
      if (gap < 1) break;
      let topUp = gap;
      for (const type of ['shares', 'accountBasedPensions', 'termDeposits']) {
        (factFind.assets![type] || []).forEach((asset: DraftAsset) => {
          if (topUp <= 0) return;
          const assetId = `${type}-${asset.id}`;
          const room = n(asset.value) - (out[assetId] || 0);
          const add = Math.min(room, topUp);
          if (add > 0) { out[assetId] = (out[assetId] || 0) + add; topUp -= add; }
        });
      }
      if (topUp >= gap) break; // nothing left to sell
    }
    return out;
  }, [liquidTargetForEntryFee, factFind?.assets]);
  // Resolved by the SAME function the allocator card renders from: the user's figure for any asset
  // they have touched, the default for every asset they have not. Previously this took the user's
  // map wholesale once it had any key at all, so dragging one slider dropped the other assets'
  // defaults from the map handed to the ENGINE while the card still showed them — the projections
  // modelled a fraction of the entry fee the client could see was fully funded.
  const effectiveEntryFeeFunding: FundingMap = useMemo(
    () => resolveRvEntryFeeFunding(factFind, entryFeeFunding, defaultEntryFeeAllocation),
    [factFind, entryFeeFunding, defaultEntryFeeAllocation],
  );
  const totalTowardEntryFee = useMemo(
    () =>
      computeRvEntryFeeFundingTotals(factFind as unknown as FactFind, effectiveEntryFeeFunding, entryFeeCgtOverrides)
        .netTotal,
    [effectiveEntryFeeFunding, entryFeeCgtOverrides, factFind]
  );
  const entryFeeAmount = villageInputs.entryFee;
  // On every route: what neither the sale nor the allocated savings cover.
  const entryFeeShortfall = liquidTargetForEntryFee > 0 ? Math.max(0, liquidTargetForEntryFee - totalTowardEntryFee) : 0;
  /*
   * KEEPING THE HOME, SAVINGS PUT TO WORK (rebuild, 21 Oct 2026): the ledger's guaranteed income, property and
   * investing, when the home is kept, are bought from savings — after the entry payment, in the usual order,
   * keeping the emergency money back. The engine draws exactly these accounts (applyRvSavingsDeployment).
   */
  const savingsDeployment = useMemo(() => {
    if (fundFrom !== 'liquid') return null;
    const d = sellHomeDetails;
    const ip = n(d.purchaseInvestmentProperty);
    const dep = {
      shares: n(d.purchaseShares),
      investmentProperty: ip,
      ipStampDuty: ip > 0 ? n(d.investmentPropertyStampDuty) : 0,
      ipRent: ip > 0 ? n(d.investmentPropertyRent) : 0,
      ipExpenses: ip > 0 ? n(d.investmentPropertyExpenses) : 0,
      ...(d.investmentPropertyOwner ? { ipOwnership: d.investmentPropertyOwner as 'client' | 'partner' | 'joint' } : {}),
      annuity: n(d.purchaseAnnuity),
      annuityType: d.rvAnnuityType === 'fixedTerm' ? 'fixedTerm' as const : d.rvAnnuityType === 'guaranteed' ? 'guaranteed' as const : 'lifetime' as const,
      annuityResidualPercent: n(d.rvAnnuityResidualPercent),
      annuityIndexation: !!d.rvAnnuityIndexation,
      annuityReversionary: !!d.rvAnnuityReversionary,
      ...(d.rvAnnuityOwner ? { annuityOwner: d.rvAnnuityOwner as 'client' | 'partner' | 'joint' } : {}),
      ...(d.rvAnnuityIncomeOverride != null ? { annuityIncomeOverride: n(d.rvAnnuityIncomeOverride) } : {}),
    };
    return dep.shares + dep.investmentProperty + dep.annuity > 0 ? dep : null;
  }, [fundFrom, sellHomeDetails]);
  const savingsIpLoan = fundFrom === 'liquid' && n(sellHomeDetails.purchaseInvestmentProperty) > 0 && n((sellHomeDetails as { ipLoanAmount?: number }).ipLoanAmount) > 0
    ? { amount: n((sellHomeDetails as { ipLoanAmount?: number }).ipLoanAmount), rate: n((sellHomeDetails as { ipLoanRate?: number }).ipLoanRate), termYears: n((sellHomeDetails as { ipLoanTermYears?: number }).ipLoanTermYears) }
    : null;
  /** Which accounts pay for it: after the entry payment's draw, bank above the emergency money, then term deposits, pensions, shares. */
  const savingsDeploymentFunding: FundingMap = useMemo(() => {
    if (!savingsDeployment) return {};
    let left = savingsDeployment.shares + savingsDeployment.investmentProperty + savingsDeployment.ipStampDuty + savingsDeployment.annuity - (savingsIpLoan?.amount ?? 0);
    const out: FundingMap = {};
    const assets = (factFind?.assets || {}) as Record<string, DraftAsset[] | undefined>;
    const bankTotal = (assets.bankAccounts || []).reduce((t, a) => t + n(a.value), 0);
    let bankRoom = Math.max(0, bankTotal - rvReserve - (assets.bankAccounts || []).reduce((t, a) => t + n(effectiveEntryFeeFunding[`bankAccounts-${a.id}`]), 0));
    for (const kind of ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares']) {
      for (const a of assets[kind] || []) {
        if (left <= 0) break;
        const key = `${kind}-${a.id}`;
        let room = Math.max(0, n(a.value) - n(effectiveEntryFeeFunding[key]));
        if (kind === 'bankAccounts') { room = Math.min(room, bankRoom); bankRoom -= room; }
        const take = Math.min(room, left);
        if (take > 0) { out[key] = take; left -= take; }
      }
    }
    return out;
  }, [savingsDeployment, savingsIpLoan?.amount, factFind?.assets, rvReserve, effectiveEntryFeeFunding]);
  const rvCoreStrategy = useMemo(
    () => ({
      ...(savingsDeployment ? { savingsDeployment, savingsDeploymentFunding, ...(savingsIpLoan ? { savingsIpLoan } : {}) } : {}),
      fundFrom,
      rentHome: rvOverallStrategy.rentHome,
      sellHomeDetails: fundFrom === 'sell_home' ? sellHomeDetails : undefined,
      rentDetails: rvOverallStrategy.rentHome ? rentDetails : undefined,
      // On every route — on a property route this is the remainder the sale did not cover.
      entryFeeFunding: effectiveEntryFeeFunding,
      entryFeeCgtOverrides,
      investmentPropertyDetails: fundFrom === 'investment_property' ? rvInvestmentPropertyDetails : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fundFrom, rvOverallStrategy.rentHome, sellHomeDetails, rentDetails, effectiveEntryFeeFunding, entryFeeCgtOverrides, rvInvestmentPropertyDetails, savingsDeployment, savingsDeploymentFunding],
  );

  const liquidFactFindAfterVillage: DraftFactFind = useMemo(
    () => buildSimulatedFactFindForVillageStrategy(factFind as unknown as FactFind, rvCoreStrategy, villageInputs).factFind,
    [factFind, rvCoreStrategy, villageInputs],
  );

  /**
   * The shared debt strategy — see the Aged Care module. Handed to the ENGINE via the strategy
   * options rather than applied here: the village projections' own year-1 financials are what feed
   * this page's after-position and impact panel, so the one wiring point keeps the projections, the
   * panel and the legacy estimate agreeing about whether the debt exists. The engine performs the
   * payoff at the projection's start — a lender takes the money any day.
   */
  /**
   * When the client moves into the village, blank = model it from today. A case fact, asked with the
   * village costs and threaded to the manual projection AND every Suggestion Engine candidate — the
   * two modes must never model the same move on different timing.
   */
  const [villageEntryDate, setVillageEntryDateState] = useState<string>(() => String((factFind?.goals as { rvEntryDate?: string } | undefined)?.rvEntryDate || ''));
  const setVillageEntryDate = (value: string) => {
    setVillageEntryDateState(value);
    setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), rvEntryDate: value } }));
  };
  const villageEntryDateInPast = villageEntryDate !== '' && villageEntryDate < new Date().toISOString().slice(0, 10);
  const effectiveVillageEntryDate = villageEntryDateInPast ? '' : villageEntryDate;

  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);

  const rvStrategyOptions: Record<string, unknown> = useMemo(() => {
    const hasOther =
      otherStrategies.gifting || otherStrategies.funeralBonds || otherStrategies.annuity;
    return {
      inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT,
      otherStrategies: hasOther ? otherStrategies : undefined,
      retirementVillage: {
        ...rvCoreStrategy,
        otherStrategies: hasOther ? otherStrategies : undefined,
        annuityRecord: otherStrategies.annuity ? annuityRecord : null,
      },
      ...(debtPayoffPlan ? { debtPayoff: debtPayoffPlan } : {}),
      ...(effectiveVillageEntryDate ? { villageEntryDate: effectiveVillageEntryDate } : {}),
    };
  }, [rvCoreStrategy, otherStrategies, annuityRecord, debtPayoffPlan, effectiveVillageEntryDate]);
  const summary: VillageCostSummary = useMemo(() => getVillageCostSummary(villageInputs), [villageInputs]);

  /** The hand-built plan's figures are still being worked out (utils/calcTracker.ts, 25 Oct 2026). */
  const { track: trackRv, working: rvCalcWorking } = useCalcTracker();
  const [projections, setProjections] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    // The payout choice rides with the village inputs (the server builds the moved balance sheet from them).
    trackRv(calculationApi.runRetirementVillageProjections(factFind, rvStrategyOptions, { ...villageInputs, oldUnitPaysAtMove: rvSetup.oldUnitPaysAtMove !== false }, localLegacyYears)).then((proj: RvProjectionRow[]) => {
      if (!cancelled) setProjections(proj);
    });
    return () => { cancelled = true; };
  }, [factFind, rvStrategyOptions, villageInputs, localLegacyYears, rvSetup.oldUnitPaysAtMove]);

  const [legacyEstimate, setLegacyEstimate] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (projections.length === 0) { setLegacyEstimate(0); return; }
      const lastRow = projections[localLegacyYears - 1];
      if (lastRow?.netWorth != null) { setLegacyEstimate(estateOf(lastRow) as number); return; }
      if (lastRow?.factFind) {
        const fin = await calculationApi.calculateFinancials(lastRow.factFind) as FinancialPosition;
        if (!cancelled) setLegacyEstimate(fin.netWorth);
        return;
      }
      setLegacyEstimate(0);
    })();
    return () => { cancelled = true; };
  }, [projections, localLegacyYears]);

  const [initialFinancials, setInitialFinancials] = useState<FinancialPosition | null>(null);
  useEffect(() => {
    let cancelled = false;
    calculationApi.calculateFinancials(factFind).then((r: FinancialPosition) => { if (!cancelled) setInitialFinancials(r); });
    return () => { cancelled = true; };
  }, [factFind]);

  const simulatedFinancialsRaw = projections.length > 0 ? projections[0]!.financials : null;
  /**
   * With a dated entry, year 1 of the projection is the PRE-entry year — the client still at home —
   * so the impact panel's "after" must come from the plan-in-place position instead (the moved
   * fact-find, assessed normally). Undated, this is exactly the year-1 financials it always was.
   */
  const [villageAfterFinancials, setVillageAfterFinancials] = useState<FinancialPosition | null>(null);
  useEffect(() => {
    if (!effectiveVillageEntryDate) { setVillageAfterFinancials(null); return undefined; }
    let cancelled = false;
    // Assessed WITH the move's means-test options (rent-the-home income, kept-home assessability) —
    // the same shared mapping the server's projection run applies, so the plan-in-place position and
    // the projections can never disagree about what the move does to the tests.
    const meansOptions = buildVillageMeansAssessmentOptions(rvCoreStrategy, villageInputs, liquidFactFindAfterVillage as unknown as FactFind);
    trackRv(calculationApi.calculateFinancials(liquidFactFindAfterVillage, meansOptions)).then((r: FinancialPosition) => { if (!cancelled) setVillageAfterFinancials(r); });
    return () => { cancelled = true; };
  }, [effectiveVillageEntryDate, liquidFactFindAfterVillage, rvCoreStrategy, villageInputs]);
  const simulatedFinancials = effectiveVillageEntryDate ? (villageAfterFinancials ?? simulatedFinancialsRaw) : simulatedFinancialsRaw;
  const [showProjectionsModal, setShowProjectionsModal] = useState(false);
  /** What "See the projections" opens: the plan, or staying put (step 2 and the rail before a plan is named). */
  const [projectionsSource, setProjectionsSource] = useState<'plan' | 'stayPut'>('plan');
  /*
   * THE FINANCIAL-YEAR / CALENDAR-YEAR TOGGLE (owner, 23 Sep 2026: missing here, present in Aged
   * Care). It is not a display filter — the engine PROJECTS the year it REPORTS, so the rows have to
   * be cut again. That second run is what the shared modal gates the toggle on, which is why passing
   * `onPeriodModeChange` is the whole of the change. Fetched only while the modal is open, so a
   * remembered 'calendar_year' does not fire a second run on every page load, and the mode is the
   * modal's own sticky preference, shared with Aged Care.
   */
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<RvProjectionRow[] | null>(null);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return undefined; }
    if (!showProjectionsModal) return undefined;
    let cancelled = false;
    // Cleared first so a changed input re-shows the hourglass rather than leaving the previous run's
    // rows on screen as though they were current.
    setCalendarYearProjections(null);
    calculationApi.runRetirementVillageProjections(
      factFind,
      { ...rvStrategyOptions, projectionPeriodMode: 'calendar_year' },
      villageInputs,
      localLegacyYears,
    ).then((proj: RvProjectionRow[]) => { if (!cancelled) setCalendarYearProjections(proj); });
    return () => { cancelled = true; };
  }, [showProjectionsModal, modalPeriodMode, factFind, rvStrategyOptions, villageInputs, localLegacyYears]);

  /** First row unchanged; adds fyFullYearView for dual-world FY blend (financial_year mode only). */
  const [projectionsForModal, setProjectionsForModal] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    if (!projections?.length) { setProjectionsForModal(projections); return undefined; }
    const mode = (rvStrategyOptions.projectionPeriodMode as string | undefined) ?? 'financial_year';
    if (mode === 'strategy_year') { setProjectionsForModal(projections); return undefined; }

    let cancelled = false;
    calculationApi.computeYearOneFullAustralianFyBlendRetirementVillage({
      baselineFactFind: factFind,
      strategyOptionsStrategy: rvStrategyOptions,
      villageInputsStrategy: villageInputs,
      strategyYearOneProjection: projections[0],
      initialFinancials,
      currentFinancials: simulatedFinancials,
      options: {},
    }).then((fyView: FyBlendView) => {
      if (!cancelled) setProjectionsForModal(projections.map((row, i) => (i === 0 ? { ...row, fyFullYearView: fyView } : row)));
    });
    return () => { cancelled = true; };
  }, [projections, rvStrategyOptions, villageInputs, factFind, initialFinancials, simulatedFinancials]);
  const noVillageScenario = (summary.entryFee === 0 && summary.annualOngoing === 0);
  const investmentPropertySaleSummary = useMemo(() => {
    if (fundFrom !== 'investment_property' || !rvInvestmentPropertyDetails || !factFind?.assets?.investmentProperty?.length) return null;
    const ip = factFind.assets.investmentProperty.find(p => String(p.id) === String(rvInvestmentPropertyDetails.selectedPropertyId)) || factFind.assets.investmentProperty[0]!;
    if (!ip) return null;
    const fullValue = n(ip.value);
    const { netProceeds, cgt } = computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, ip as unknown as FactFindAsset, fullValue);
    return { netProceeds, estimatedCGT: cgt };
  }, [fundFrom, rvInvestmentPropertyDetails, factFind]);
  const totalEntryFeeCgtFromLiquidIp = useMemo(() => {
    if (fundFrom !== 'liquid' || !effectiveEntryFeeFunding || !factFind?.assets?.investmentProperty) return 0;
    let total = 0;
    Object.entries(effectiveEntryFeeFunding).forEach(([assetId, amount]) => {
      if (!assetId.startsWith('investmentProperty-') || !n(amount)) return;
      const idRaw = assetId.replace(/^investmentProperty-/, '');
      const asset = factFind.assets!.investmentProperty!.find(a => String(a.id) === idRaw);
      if (asset) total += computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, asset as unknown as FactFindAsset, Math.min(n(amount), n(asset.value))).cgt;
    });
    return total;
  }, [fundFrom, effectiveEntryFeeFunding, factFind]);
  const entryFeeCgtForModal = (investmentPropertySaleSummary?.estimatedCGT ?? 0) + (totalEntryFeeCgtFromLiquidIp ?? 0);
  const [rvImpactBreakdowns, setRvImpactBreakdowns] = useState<{ agePension: BreakdownRow[]; cashFlow: BreakdownRow[]; netWorth: BreakdownRow[]; legacy: BreakdownRow[] }>({ agePension: [], cashFlow: [], netWorth: [], legacy: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
    const fmt = (v: unknown): string => (v == null || v === '' || isNaN(Number(v))) ? '$0' : '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const y0 = projections[0];
    const lastProj = projections[localLegacyYears - 1];
    const agePension: BreakdownRow[] = (y0?.financials?.socialSecurityBreakdown && Array.isArray(y0.financials.socialSecurityBreakdown) && y0.financials.socialSecurityBreakdown.length > 0)
      ? y0.financials.socialSecurityBreakdown
      : (initialFinancials?.socialSecurityBreakdown && Array.isArray(initialFinancials.socialSecurityBreakdown))
        ? initialFinancials.socialSecurityBreakdown
        : [{ label: 'Age Pension (p.a.)', value: fmt(y0?.financials?.socialSecurity ?? initialFinancials?.socialSecurity) }];
    const ipCgt = (investmentPropertySaleSummary?.estimatedCGT ?? 0) + (totalEntryFeeCgtFromLiquidIp ?? 0);
    // CGT is NOT a cashflow item. Under Option A (net proceeds pool) it is deducted from the sale
    // proceeds before they are allocated, so it reduces net worth at settlement and never reaches
    // `netCashflow`. It used to be emitted as a "Less:" row ABOVE the total, which made the rows
    // fail to reconcile against the stated net cashflow and read as though cashflow bore the tax.
    // It now sits below the total, as reference only.
    const cashFlow: BreakdownRow[] = (y0?.financials?.cashFlowBreakdown && Array.isArray(y0.financials.cashFlowBreakdown))
      ? [
          ...y0.financials.cashFlowBreakdown,
          ...(summary.annualOngoing > 0 ? [{ label: '--- Village costs ---', value: '' }, { label: 'Less: Village ongoing fees (p.a.)', value: -summary.annualOngoing }] : []),
          { label: '--- Net cashflow (Year 1) ---', value: '' },
          { label: 'Net cashflow', value: y0?.netCashflow ?? 0 },
          ...(ipCgt > 0
            ? [
                { label: '--- Not in cashflow ---', value: '' },
                { label: 'CGT on investment property sale (paid from sale proceeds)', value: fmt(ipCgt) },
              ]
            : []),
        ]
      : [{ label: 'Net cashflow (Year 1)', value: fmt(y0?.netCashflow) }];
    type AssetsMapLike = Record<string, DraftAsset[] | undefined>;
    const netWorthFromAssets = (formDataOrAssets: DraftFactFind | AssetsMapLike | null | undefined): BreakdownRow[] => {
      const assets: AssetsMapLike | undefined = (formDataOrAssets as DraftFactFind)?.assets || (formDataOrAssets as AssetsMapLike | undefined);
      if (!assets) return [];
      const types = ['bankAccounts', 'termDeposits', 'shares', 'accountBasedPensions'];
      const out: BreakdownRow[] = [];
      types.forEach(t => {
        const arr = assets[t] || [];
        if (t === 'bankAccounts' && arr.some(a => a.id === 'rv-sell-residual')) {
          const residualEntry = arr.find(a => a.id === 'rv-sell-residual');
          const residualVal = n(residualEntry?.value);
          const otherBank = arr.filter(a => a.id !== 'rv-sell-residual').reduce((s, a) => s + n(a.value), 0);
          if (otherBank > 0) out.push({ label: 'Bank accounts', value: fmt(otherBank) });
          if (residualVal > 0) out.push({ label: 'Residual from home sale (to bank)', value: fmt(residualVal) });
        } else {
          const sum = arr.reduce((s, a) => s + n(a.value), 0);
          if (sum > 0) out.push({ label: (t === 'bankAccounts' ? 'Bank accounts' : t === 'termDeposits' ? 'Term deposits' : t === 'accountBasedPensions' ? 'Account-based pensions' : 'Shares'), value: fmt(sum) });
        }
      });
      const total = out.reduce((s, o) => s + (typeof o.value === 'number' ? o.value : parseFloat(String(o.value).replace(/[$,]/g, '')) || 0), 0);
      if (out.length) out.push({ label: '--- Total (liquid) ---', value: '' }, { label: 'Total', value: fmt(total) });
      return out;
    };
    const netWorth = y0?.factFind ? netWorthFromAssets(y0.factFind) : netWorthFromAssets(factFind);
    const legacyAssets = lastProj?.factFind?.assets;
    const legacyBreakdown: BreakdownRow[] = legacyAssets
      ? [
          ...netWorthFromAssets(lastProj!.factFind),
          ...((lastProj?.exitProceeds ?? 0) > 0 ? [{ label: '--- Village exit ---', value: '' }, { label: 'Exit entitlement received', value: fmt(lastProj!.exitProceeds) }] : [])
        ]
      : [{ label: 'Legacy estimate', value: fmt(legacyEstimate) }];
    let netWorthFinal = netWorth;
    if (!netWorth.length) {
      const fallbackNetWorth = y0?.factFind ? (await calculationApi.calculateFinancials(y0.factFind) as FinancialPosition).netWorth : initialFinancials?.netWorth;
      netWorthFinal = [{ label: 'Net Worth (Year 1)', value: fmt(fallbackNetWorth) }];
    }
    if (!cancelled) setRvImpactBreakdowns({ agePension, cashFlow, netWorth: netWorthFinal, legacy: legacyBreakdown });
    })();
    return () => { cancelled = true; };
  }, [projections, summary.entryFee, summary.annualOngoing, localLegacyYears, initialFinancials, factFind, legacyEstimate, investmentPropertySaleSummary, totalEntryFeeCgtFromLiquidIp]);
  const legacyEstimateDisplay = legacyEstimate;

  const [netWorthAfterDisplay, setNetWorthAfterDisplay] = useState<number | undefined>(0);
  useEffect(() => {
    // The engine's own rows now carry the accrued exit entitlement in net worth (the village plugin
    // values the contract each resident-year — the refundable-RAD principle), so this reads the
    // engine's figure directly. The old path recomputed net worth from the raw fact-find (which
    // cannot know about the contract) and added a capped entitlement back on — a UI-side patch that
    // would now double count.
    if (noVillageScenario) { setNetWorthAfterDisplay(initialFinancials?.netWorth); return; }
    /*
     * The tile says "TODAY", so it must show the position the day the plan is done — not year 1's
     * CLOSING figure, which is what it used to read (owner-caught, 2026-08-26: "I'm not clear how
     * the net worth has gone up from $2,365,000 to $2,369,997 given we started with the same money
     * and have incurred selling costs of $50k"). It had gone up because a full year of investment
     * growth was folded into a number labelled "today", masking the immediate cost of the move.
     *
     * `openingNetWorth` is that day-one position — the moved balance sheet including the refundable
     * entry contribution — so the tile now shows the real immediate effect. On the owner's scenario
     * that is $2,365,000 -> $2,315,000: down by the selling costs, and nothing else, because paying
     * an entry contribution is an asset swap rather than a loss.
     */
    const dayOne = projections[0]?.openingNetWorth;
    setNetWorthAfterDisplay(typeof dayOne === 'number' ? dayOne : initialFinancials?.netWorth);
  }, [noVillageScenario, projections, initialFinancials]);

  /**
   * The STAY-PUT estate — what this client would be worth at the horizon if they did nothing.
   *
   * Owner-caught (2026-08-26): the plan summary compared the projected estate against TODAY'S net
   * worth, while the Suggestion Engine compares it against the stay-put projection. Those are not
   * the same benchmark and they can disagree about the direction: a plan can leave more than the
   * client is worth today (five years of growth will do that on its own) while leaving materially
   * LESS than simply staying home would have. Measuring the move against today's wealth credits the
   * move with growth it did not cause, which is exactly the wrong way for a comparison to be wrong.
   *
   * One projection, the same call the engine's own baseline candidate makes: the village zeroed out.
   */
  /*
   * Kept WHOLE, not just its last row (24 Sep 2026): the stay-put projection is now the position
   * step 2 shows, the runway the rail carries until a plan is chosen, and what "See the projections"
   * opens in that state — one run, read everywhere staying put is quoted. Independent of the village
   * inputs: the village is zeroed out of it by definition.
   */
  const [stayPutProjections, setStayPutProjections] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    trackRv(calculationApi
      .runRetirementVillageProjections(
        factFind,
        { inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT, retirementVillage: {} },
        { entryFee: 0, ongoingWeeklyOrMonthly: 0, ongoingFrequency: 'monthly', dmfPctPerYear: 0, dmfCapYears: 0, yearsResidency: 0, growthRatePct: 0, capitalGainShareToResident: 0.5, reinstatementCap: 0 },
        localLegacyYears,
      ))
      .then((rows: RvProjectionRow[]) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => {
    const last = stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1];
    return estateOf(last);
  }, [stayPutProjections, localLegacyYears]);


  /*
   * An ENGINE plan's levers, in Manual Control's terms (village review, 18 Oct 2026: a sell-the-home
   * plan saved from the engine reopened as paid from savings, because the engine candidate carries
   * `fundFrom` / `sellHomeDetails` / `entryFeeFundingOverride` / `investmentPropertyDetails` and the
   * restore read only Manual Control's own keys). Also used when Manual Control is opened with an
   * engine plan selected, so "your selection carries over" is true.
   */
  const seedManualFromEngineInputs = (inputs: Record<string, unknown>) => {
    const fundFrom = inputs.fundFrom as string | undefined;
    if (!fundFrom) return false;
    setRvOverallStrategy({ downsizeHome: fundFrom === 'sell_home', rentHome: !!inputs.rentHome, sellInvestmentProperty: fundFrom === 'investment_property' });
    if (inputs.sellHomeDetails) setSellHomeDetails((prev) => ({ ...prev, ...(inputs.sellHomeDetails as Partial<SellHomeDetails>) }));
    if (inputs.rentDetails) setRentDetails((prev) => ({ ...prev, ...(inputs.rentDetails as Partial<RentDetails>) }));
    setEntryFeeFunding((inputs.entryFeeFundingOverride as FundingMap) || {});
    if (inputs.investmentPropertyDetails) setRvInvestmentPropertyDetails((prev) => ({ ...prev, ...(inputs.investmentPropertyDetails as Partial<RvInvestmentPropertyDetails>) }));
    // A plan that also clears the debts brings its payoff with it (25 Oct 2026), or the hand-built figures
    // open short of the card's by the repayments.
    const payoff = inputs.debtPayoff as DebtPayoff | undefined;
    setDebtPayments({ ...(payoff?.payments || {}) });
    setDebtFunding({ ...((payoff?.funding || {}) as Record<string, number>) });
    setDebtRepaymentBasis(payoff?.repaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment');
    return true;
  };

  const reopenStrategy = (strategy: SavedStrategy) => {
    const inputs = strategy.inputs || {};
    if (inputs.villageInputs) setVillageInputs(inputs.villageInputs as VillageInputs);
    if (!inputs.rvOverallStrategy && seedManualFromEngineInputs(inputs as Record<string, unknown>)) {
      setEntryFeeCgtOverrides({});
      setStrategyMode('manual');
      setShowStrategyOptions(true);
      setActiveSavedStrategyId(strategy.id);
      return;
    }
    setRvOverallStrategy((inputs.rvOverallStrategy as RvOverallStrategy) || { rentHome: false, downsizeHome: false, sellInvestmentProperty: false });
    setSellHomeDetails((inputs.sellHomeDetails as SellHomeDetails) || sellHomeDetails);
    setRentDetails((inputs.rentDetails as RentDetails) || rentDetails);
    setEntryFeeFunding((inputs.entryFeeFunding as FundingMap) || {});
    setEntryFeeCgtOverrides((inputs.entryFeeCgtOverrides as Record<string, number | null>) || {});
    setRvInvestmentPropertyDetails((inputs.rvInvestmentPropertyDetails as RvInvestmentPropertyDetails) || rvInvestmentPropertyDetails);
    const restoredOtherStrategies = inputs.otherStrategies as OtherStrategies | undefined;
    setOtherStrategies(
      restoredOtherStrategies || {
        gifting: false,
        giftingAmount: 0,
        giftingSource: 'bankAccounts',
        funeralBonds: false,
        funeralBondAmount: 0,
        funeralBondSource: 'bankAccounts',
        annuity: false,
      },
    );
    setAnnuityRecord((inputs.annuityRecord as AnnuityDetails) || null);
    if (restoredOtherStrategies?.annuity || inputs.annuityRecord) setShowOtherStrategies(true);
    setStrategyMode('manual');
    setShowStrategyOptions(true);
    setActiveSavedStrategyId(strategy.id);
  };

  const rvSavedStrategies = savedStrategies?.filter((s) => s.type === 'retirement_village') || [];
  const hasHome = !!(factFind?.home && n(factFind.home.value) > 0);
  // Matches the suggestion engine's own test for generating a sell-IP candidate
  // (generateRetirementVillageCandidates), so the narrative and the strategies can never disagree
  // about whether this client has an investment property to sell.

  /**
   * One pool, two claimants. A dollar can pay the village entry fee or retire a debt, never both, so
   * each card's sliders are capped by what the OTHER has already claimed of that asset — the aged
   * care module's shared-pool rule (see DebtPayoffStrategy's header). Without this the debt card
   * offered an asset's FULL balance even after the entry fee had spent most of it.
   */
  const reservedForEntryFee = useMemo(
    () => Object.fromEntries(Object.entries(effectiveEntryFeeFunding || {}).map(([key, amount]) => [key, n(amount)])),
    [effectiveEntryFeeFunding],
  );
  const reservedForDebt = useMemo(
    () => Object.fromEntries(Object.entries(debtFunding || {}).map(([key, amount]) => [key, n(amount)])),
    [debtFunding],
  );
  const entryFeeFundingFromIp = useMemo(() => {
    const funding = effectiveEntryFeeFunding || {};
    return Object.keys(funding).some(id => id.startsWith('investmentProperty-') && n(funding[id]) > 0);
  }, [effectiveEntryFeeFunding]);

  const entryFeeAllocationBreakdown: { label: string; amount: number }[] = useMemo(() => {
    if (fundFrom !== 'liquid' || summary.entryFee <= 0 || !factFind?.assets) return [];
    const order = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares', 'investmentProperty'];
    const typeLabels: Record<string, string> = { bankAccounts: 'Bank accounts', termDeposits: 'Term deposits', accountBasedPensions: 'Account-based pensions', shares: 'Shares', investmentProperty: 'Investment property' };
    const out: { label: string; amount: number }[] = [];
    order.forEach(type => {
      const arr = factFind.assets![type];
      if (!arr) return;
      (arr || []).forEach(asset => {
        const assetId = `${type}-${asset.id}`;
        const amount = n(effectiveEntryFeeFunding?.[assetId]);
        if (amount <= 0) return;
        const label = asset.description || typeLabels[type] || type;
        out.push({ label, amount });
      });
    });
    return out;
  }, [fundFrom, summary.entryFee, factFind?.assets, effectiveEntryFeeFunding]);

  /*
   * GOAL LEADERS, with the aged care horizon rule (owner, 23 Sep 2026: "a flagged strategy should
   * not win a ranking"; 24 Sep: the nil-residual annuity led "best cashflow" here). A candidate
   * whose annuity needs more years than this client's age and health point to is set aside from
   * the leadership — never hidden: the disclosure below the cards names it and opens it.
   */
  const rvRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!rvSuggestions) return [];
    const movers = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).filter((s) => s.id !== 'rv-stay-home' && !(s.inputs as Record<string, unknown> | undefined)?.stayHome);
    const eligible = movers.filter((s) => annuityHorizonWarningOf(s, factFind) == null);
    /*
     * RANKED AMONG THE WAYS OF MOVING, not against staying put (village rebuild, owner 20–21 Oct 2026).
     * The shared ranking withholds a plan that does not beat doing nothing; for a village that is most
     * families, since a move usually costs the estate something. They are moving for other reasons, so
     * the screen shows the least costly ways to do it, with the price said in one line above the cards
     * and an "Important" line on each — never as if the move were free.
     */
    return buildRecommendedOptimalStrategies(eligible, null, { cashflowTarget: Number(rvSetup.cashflowTarget) || 0, readings: candidateReadings });
  }, [rvSuggestions, factFind, rvSetup.cashflowTarget]);
  const rvGoalLeaders: RvEvaluatedStrategy[] = useMemo(() => mergeGoalLeaderStrategies(rvRecommended) as RvEvaluatedStrategy[], [rvRecommended]);
  /** Whether the cashflow-covered leader is also the best estate outright — said, not shown twice. */
  const coveredSameAsEstate = useMemo(() => coveredIsBestEstate(rvRecommended), [rvRecommended]);
  /** The cashflow-covered leader's case, for the line on its card. */
  const coveredNoteFor = (s: RvEvaluatedStrategy): string | null => {
    const v = rvRecommended.find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === s.name)?.cashflowCovered;
    return v ? cashflowCoveredNote(v, Number(localLegacyYears) || 5, formatCurrency) : null;
  };
  /** The leaders the horizon rule set aside, so the screen can say so and open them. */
  const rvSetAside = useMemo(() => {
    if (!rvSuggestions) return [] as Array<{ strategy: RvEvaluatedStrategy; labels: string[]; warning: string }>;
    return (mergeGoalLeaderStrategies(rvSuggestions.recommended) as RvEvaluatedStrategy[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: annuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [rvSuggestions, factFind]);
  /** Which goal's card is shown — aged care's chips (presentation only; the engine is not re-run). */
  const [optimisationFocus, setOptimisationFocus] = useState<'covered' | 'legacy' | 'cashflow' | 'pension' | 'all'>('legacy');
  /** How the full list is ordered — aged care's sort. */
  const [allSortBy, setAllSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
  const selectedStrategy: RvEvaluatedStrategy | null = useMemo(
    // Search the FULL evaluated set, not just the goal leaders — "Compare all strategies" lets the
    // user select any evaluated strategy, not only the three leaders.
    () => (strategyMode === 'optimised'
      ? ((((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).find((s) => s.id === (explainSeId ?? selectedSeId))) || rvGoalLeaders.find((s) => s.id === (explainSeId ?? selectedSeId)) || null)
      : null),
    [strategyMode, rvSuggestions, rvGoalLeaders, selectedSeId, explainSeId],
  );

  /**
   * Which strategy CARDS appear, and what each is badged as.
   *
   * Owner-caught (2026-08-26), two faults with one cause — the screen only ever rendered the goal
   * leaders:
   *   1. A run where nothing beat staying put on estate or Age Pension showed a single card. The
   *      withholding is honest (recommending something worse than doing nothing would not be), but a
   *      client who has already decided to move still needs to know which option is LEAST damaging.
   *      So a goal with no improver now shows its best-of-field, badged as not beating the baseline
   *      rather than presented as a recommendation.
   *   2. Selecting a strategy from "All modelled strategies" set the selection correctly and drove
   *      the panels — but if it was not a goal leader it had no card, so the screen looked unchanged
   *      and read as though the click had been ignored.
   */
  const seCards = useMemo(() => {
    if (!rvSuggestions) return [] as { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string; tradeOffNote?: string }[];
    const LABELS: Record<string, string> = { ...GOAL_CHIP_LABELS };
    const movers = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).filter((s) => !(s.inputs as Record<string, unknown> | undefined)?.stayHome);
    const out: { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string; tradeOffNote?: string }[] = [];
    const byId = new Map<string, { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string; tradeOffNote?: string }>();
    const second = secondPlanOf(rvRecommended, formatCurrency);
    const leadWords = leadPlanOf(rvRecommended, formatCurrency);
    const push = (strategy: RvEvaluatedStrategy, badges: string[], belowBaseline?: string) => {
      const key = String(strategy.id ?? strategy.name ?? '');
      const existing = byId.get(key);
      if (existing) { badges.forEach((b) => { if (!existing.badges.includes(b)) existing.badges.push(b); }); return; }
      const entry = { strategy, badges: [...badges], belowBaseline };
      byId.set(key, entry);
      out.push(entry);
    };

    // A leader of the MOVERS can still leave less than staying put: the card says so, neutrally
    // (owner, 19 Oct 2026 — awareness, not a mistake; see belowBaselineWords).
    rvGoalLeaders.forEach((leader) => {
      const labels = leader.recommendationLabels || [];
      const short = labels.map((l) => belowBaselineWords(l, leader as never, rvSuggestions.stayHome as never, 'staying put', formatCurrency)).find(Boolean);
      push(leader, labels.map((l) => LABELS[l] || l), short);
      // The chip says what the card is, and the line why (planChoiceRule.ts).
      const key = String(leader.id ?? leader.name ?? '');
      const e = byId.get(key);
      if (e) { applyPlanChoiceWords(e, key, second, SECOND_PLAN_CHIP); applyPlanChoiceWords(e, key, leadWords, BEST_ESTATE_CHIP); }
    });

    const base = rvSuggestions.stayHome?.snapshot;
    const GOALS = [
      { label: 'Optimal Legacy', friendly: 'estate', pick: (s: RvEvaluatedStrategy) => s.snapshot?.legacyValue ?? 0, baseValue: base?.legacyValue ?? 0 },
      { label: 'Optimal Cashflow', friendly: 'cashflow', pick: (s: RvEvaluatedStrategy) => s.snapshot?.netCashflow ?? 0, baseValue: base?.netCashflow ?? 0 },
      { label: 'Optimal Age Pension', friendly: 'Age Pension', pick: (s: RvEvaluatedStrategy) => s.snapshot?.pension ?? 0, baseValue: base?.pension ?? 0 },
    ] as const;
    GOALS.forEach(({ label, friendly, pick, baseValue }) => {
      if (movers.length === 0) return;
      if (rvGoalLeaders.some((l) => (l.recommendationLabels || []).includes(label))) return;
      // Every option at nil pension is a finding, not a shortlist — the note below says so, and a
      // "best available $0" card would add nothing.
      if (label === 'Optimal Age Pension' && movers.every((s) => (s.snapshot?.pension ?? 0) <= 0)) return;
      const best = movers.reduce((top, s) => (pick(s) > pick(top) ? s : top), movers[0]!);
      push(best, [`Best available ${friendly}`], belowBaselineWords(label, best as never, rvSuggestions.stayHome as never, 'staying put', formatCurrency)
        ?? `Does not beat staying put (${formatCurrency(baseValue)}); the closest of the options modelled on this goal.`);
    });

    if (selectedSeId && !byId.has(String(selectedSeId))) {
      const chosen = movers.find((s) => s.id === selectedSeId);
      if (chosen) push(chosen, ['Your selection']);
    }
    return out;
  }, [rvSuggestions, rvGoalLeaders, selectedSeId, rvRecommended]);
  /*
   * `usingSe`: a Suggestion Engine strategy is selected, which drives its EXPLANATION (the recap
   * behind "Explain this") and its deep dive. It never drives the rail: in engine mode the plan
   * summary holds STAYING PUT while every card carries its own figures — the aged care pattern,
   * adopted here on 24 Sep 2026 (owner: "replicate ... the interplay with the Your Plan Summary").
   */
  const usingSe = strategyMode === 'optimised' && !!selectedStrategy;
  /*
   * BUILD A PLAN YOURSELF (rebuild M3, 21 Oct 2026): the ledger's own state — which plan it started from,
   * the last change and its effect, and whether this hand-built plan is the chosen one.
   */
  const [manualStartedFrom, setManualStartedFrom] = useState<string | null>(null);
  const [manualLastChange, setManualLastChange] = useState<string | null>(null);
  const [manualChosen, setManualChosen] = useState(false);
  const noteManualChange = (words: string) => { setManualLastChange(words); setManualChosen(false); };
  const manualRent = useMemo(() => {
    const g = (factFind?.goals || {}) as { rvRentPerWeek?: number; rvRentCosts?: number; rvVacancyWeeks?: number };
    return {
      weekly: g.rvRentPerWeek != null ? n(g.rvRentPerWeek) : n(factFind?.home?.likelyRent),
      costs: g.rvRentCosts != null ? n(g.rvRentCosts) : n(factFind?.home?.likelyExpenses),
      vacancy: g.rvVacancyWeeks != null ? n(g.rvVacancyWeeks) : DEFAULT_RV_VACANCY_WEEKS,
    };
  }, [factFind?.goals, factFind?.home?.likelyRent, factFind?.home?.likelyExpenses]);
  /** The rented home's figures from step 3's answers, less the weeks it sits empty. */
  const syncRentFromAnswers = () => setRentDetails((prev) => ({ ...prev, rentAmount: rentAfterVacancy(manualRent.weekly, manualRent.vacancy), rentExpenses: manualRent.costs }));
  /** "A blank plan": the home kept, nothing moved, every amount the client's to choose. */
  const startBlankManualPlan = () => {
    setRvOverallStrategy({ downsizeHome: false, rentHome: false, sellInvestmentProperty: false });
    const zeros: FundingMap = {};
    for (const kind of ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares']) {
      for (const a of ((factFind?.assets as Record<string, DraftAsset[] | undefined> | undefined)?.[kind] || [])) zeros[`${kind}-${a.id}`] = 0;
    }
    setEntryFeeFunding(zeros);
    setSellHomeDetails((prev) => ({ ...prev, downsizerContribution: 0, partnerDownsizerContribution: 0, commenceIncomeStream: false, giftingAmount: 0, funeralBondPurchase: 0, purchaseAnnuity: 0, purchaseInvestmentProperty: 0, investmentPropertyStampDuty: 0, purchaseShares: 0 }));
    setOtherStrategies((prev) => ({ ...prev, gifting: false, giftingAmount: 0, funeralBonds: false, funeralBondAmount: 0, annuity: false }));
    setDebtPayments({});
    setDebtFunding({});
    setDebtRepaymentBasis('keep_repayment');
    setEntryFeeCgtOverrides({});
  };
  /**
   * TWO DOORS INTO THE LEDGER (owner, 25 Oct 2026). "Build a plan yourself" (no plan) opens a BLANK plan —
   * it used to open on whatever was last there. "Customise this plan" (a tested plan) clears first, then
   * copies that plan in, so nothing from an earlier hand-built plan lingers under it.
   */
  const openRvBuild = (row: RvEvaluatedStrategy | null) => {
    startBlankManualPlan();
    if (row?.inputs && seedManualFromEngineInputs(row.inputs as Record<string, unknown>)) setManualStartedFrom(rvPlanWords(row, factFind, summary.entryFee).title);
    else setManualStartedFrom(null);
    setManualLastChange(null);
    setManualChosen(false);
    setRvAsking(false);
    setStrategyMode('manual');
    setShowStrategyOptions(true);
    scrollAppToTop();
  };
  /** The hand-built plan as a tested plan would be read: its words and its risks. */
  const manualAsCandidate = (): RvEvaluatedStrategy => ({
    id: 'rv-manual',
    name: rvPlanSummaryName || 'A plan built by hand',
    inputs: {
      fundFrom,
      rentHome: rvOverallStrategy.rentHome,
      rentDetails: rvOverallStrategy.rentHome ? rentDetails : undefined,
      sellHomeDetails: fundFrom === 'sell_home' ? sellHomeDetails : undefined,
      entryFeeFundingOverride: effectiveEntryFeeFunding,
      investmentPropertyDetails: fundFrom === 'investment_property' ? rvInvestmentPropertyDetails : undefined,
    },
  } as unknown as RvEvaluatedStrategy);
  const manualPlanWords = () => rvPlanWords(manualAsCandidate(), factFind, summary.entryFee);
  const manualRiskKinds = (): RiskKind[] => {
    const k: RiskKind[] = ['villageEntry'];
    const d = sellHomeDetails;
    if (fundFrom === 'sell_home') {
      k.push('sellHome');
      if (n(d.downsizerContribution) + n(d.partnerDownsizerContribution) > 0) k.push('superContribution');
      if (d.commenceIncomeStream) k.push('accountBasedPension');
      if (n(d.purchaseAnnuity) > 0) k.push(d.rvAnnuityType === 'guaranteed' ? 'annuityGuaranteed' : n(d.rvAnnuityResidualPercent) > 0 ? 'annuityReturned' : 'annuityNil');
      if (n(d.purchaseInvestmentProperty) > 0) k.push('investmentProperty');
      if (n(d.purchaseShares) > 0) k.push('shares');
      if (n(d.giftingAmount) > 0) k.push('gift');
      if (n(d.funeralBondPurchase) > 0) k.push('funeralBond');
    } else {
      k.push('fromSavings');
      if (rvOverallStrategy.rentHome) k.push('rentFormerHome');
      if (fundFrom === 'investment_property') k.push('sellInvestmentProperty');
      if (otherStrategies.gifting) k.push('gift');
      if (otherStrategies.funeralBonds) k.push('funeralBond');
    }
    if (debtPayoffPlan) k.push('clearDebt');
    return k;
  };
  /** Choosing the hand-built plan: the same card record a tested plan saves (Manual Control parity). */
  const chooseManualPlan = () => {
    const words = manualPlanWords();
    const villageName = String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || '').trim();
    const horizon = Number(localLegacyYears) || 10;
    const exitAtHorizon = exitAfter(villageInputs, horizon);
    const card: RvPlanCardRecord = {
      plan: { legacy: n(legacyEstimateDisplay), pension: n(simulatedFinancials?.socialSecurity), cashflow: n(projections[0]?.netCashflow) },
      baseline: initialFinancials ? { legacy: n(manualBaselineLegacy), pension: n(initialFinancials.socialSecurity), cashflow: n(initialFinancials.cashFlow) } : null,
      words,
      village: { name: villageName, entryFee: summary.entryFee, annualOngoing: summary.annualOngoing, fees: n(villageInputs.ongoingWeeklyOrMonthly), feesFrequency: villageInputs.ongoingFrequency, departureFee: exitAtHorizon.dmf, backAfter: exitAtHorizon.netToResident },
      horizonYears: horizon,
      riskKinds: manualRiskKinds(),
      rentNet: rvOverallStrategy.rentHome ? Math.max(0, n(rentDetails.rentAmount) * 52 - n(rentDetails.rentExpenses)) : 0,
      origin: 'manual',
      // A hand-built guaranteed income: the family table from this plan's own rows, no twin to compare (E4/M4).
      family: rvFamilyRecordOf(rvGuaranteedIncomeOf({ inputs: { sellHomeDetails } }, factFind), factFind, projections as unknown[], null, horizon),
      // The card's runway sentence and the plan's own risks, frozen with it for the plan page (22 Oct 2026).
      runLine: projections.length ? runwayCardLine({ rows: projections as never, factFind, horizonYears: horizon, spentOn: 'the entry payment' }) : null,
      riskSpecifics: manualRiskSpecifics(),
    };
    const id = `rv-manual-${Date.now()}`;
    setSavedStrategies((prev) => withChosenRvPlan(prev as never, {
      id,
      name: words.title,
      displayName: words.title,
      type: 'retirement_village',
      source: 'manual',
      inputsFingerprint: profileFingerprint(factFind),
      // Built by the client from a tested plan ("Customise this plan", 25 Oct 2026).
      ...(manualStartedFrom ? { customisedFrom: manualStartedFrom } : {}),
      inputs: {
        card,
        unitKey: rvUnitKey(villageName, summary.entryFee),
        legacyYears: horizon,
        planContext: { entryFee: summary.entryFee, annualOngoing: summary.annualOngoing, villageName },
        villageInputs: { ...villageInputs },
        rvOverallStrategy, sellHomeDetails, rentDetails, entryFeeFunding: effectiveEntryFeeFunding, entryFeeCgtOverrides, rvInvestmentPropertyDetails, otherStrategies, annuityRecord,
        // Who decided the draw: the client's typed amounts, or our starting point left standing.
        entryFeeFundingProvenance: { source: isAdviserAllocatedEntryFee(entryFeeFunding) ? 'adviser_allocated' : 'tool_default_accepted', recordedAt: new Date().toISOString(), resolvedAllocation: { ...effectiveEntryFeeFunding } },
      },
      outputs: { summary, entryFee: summary.entryFee, annualOngoing: summary.annualOngoing, agePension: card.plan.pension, cashFlow: card.plan.cashflow, legacyEstimate: card.plan.legacy, netWorthSchedule: buildNetWorthSchedule(projections || []) },
      createdAt: new Date().toISOString(),
    } as never) as never);
    setActiveSavedStrategyId(id);
    setManualChosen(true);
  };
  /** The plan chosen on an earlier visit, for its card (one plan per unit). */
  // Read off THE chosen plan (the one the plan page shows), never off another record still flagged chosen —
  // see rvChosenEngineIdOf (25 Oct 2026). A hand-built chosen plan has no tested twin.
  const rvChosenEngineId = useMemo(() => rvChosenEngineIdOf(chosenRvPlanOf(savedStrategies as never)), [savedStrategies]);
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  // Escape closes "Every way we tested" (review, 18 Oct 2026).
  useEffect(() => {
    if (!showAllSeStrategies) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowAllSeStrategies(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAllSeStrategies]);
  /** The cards the chosen goal shows; a chosen card that leads nothing shows under every goal. */
  const visibleSeCards = useMemo(() => {
    if (optimisationFocus === 'all') return seCards;
    // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
    if (optimisationFocus === 'covered' && coveredSameAsEstate) return [];
    const badgesFor: Record<'covered' | 'legacy' | 'cashflow' | 'pension', string[]> = {
      covered: [CASHFLOW_COVERED_CHIP],
      legacy: [BEST_ESTATE_CHIP, 'Best available estate', SECOND_PLAN_CHIP],
      cashflow: ['Best cashflow', 'Best available cashflow'],
      pension: ['Best Age Pension', 'Best available Age Pension'],
    };
    return seCards.filter((c) => c.badges.some((b) => badgesFor[optimisationFocus].includes(b) || (optimisationFocus === 'legacy' && isEstateChip(b)) || b === 'Your selection'));
  }, [seCards, optimisationFocus, coveredSameAsEstate]);
  /**
   * A real annuity quote, replacing the tool's indicative rate across the whole Suggestion Engine
   * field (owner, 2026-08-26). Null until the adviser enters one, in which case every candidate that
   * buys an annuity is re-modelled and the goals re-ranked on the quoted income — a provider's
   * actual figure can easily change which strategy wins, so it must re-rank rather than just relabel.
   */
  // A provider's quote belongs to one type and one amount (annuityQuote.ts, guaranteed income review M5).
  const [seAnnuityQuote, setSeAnnuityQuote] = useState<AnnuityQuote | null>(null);
  const [seAnnuityQuoteDraft, setSeAnnuityQuoteDraft] = useState<number | ''>('');
  const effProjections: RvProjectionRow[] = usingSe ? (selectedStrategy!.projections || []) : projections;
  const effFundFrom = usingSe ? (selectedStrategy!.inputs?.fundFrom || fundFrom) : fundFrom;

  /*
   * HOW LONG THE MONEY LASTS — the section every other module's rail carries, and the one this
   * module needed most (2026-08-27). A village move commonly turns a small surplus into a deficit
   * of tens of thousands a year: the entry contribution leaves savings, the ongoing fees start, and
   * the Age Pension often goes with the home exemption. Reporting all three without ever saying how
   * many years the savings then last leaves out the question the client is actually asking.
   */
  /**
   * What the plan summary is describing, in the client's words — or null when it is describing
   * STAYING PUT. Manual Control only: the Suggestion Engine's cards each carry their own plan and
   * the rail holds the baseline beside them. Derived from the choices actually made, never a stored
   * label: a panel headed with a name that no longer matches the figures underneath is worse than
   * one with no name at all.
   *
   * PAYING FROM SAVINGS IS A CHOICE, AND IS NAMED (owner, 24 Sep 2026). The rail used to head "No
   * plan yet" over a plan the app had chosen — the entry contribution taken from savings in a
   * default order. In Manual Control that funding is the plan, so it is named as one.
   */
  const rvPlanSummaryName = useMemo<string | null>(() => {
    if (strategyMode !== 'manual') return null;
    const parts: string[] = [];
    if (rvOverallStrategy.downsizeHome) parts.push('Sell the family home');
    if (rvOverallStrategy.rentHome) parts.push('Rent the family home');
    if (rvOverallStrategy.sellInvestmentProperty) parts.push('Sell the investment property');
    if (!parts.length && summary.entryFee > 0) parts.push('Pay the entry contribution from savings');
    if (otherStrategies.annuity) parts.push('Commence an annuity');
    if (otherStrategies.gifting) parts.push('Gift to loved ones');
    if (otherStrategies.funeralBonds) parts.push('Buy a funeral bond');
    if (!parts.length) return null;
    const [lead, ...rest] = parts;
    return rest.length
      ? `${lead}, ${rest.map((p) => p.charAt(0).toLowerCase() + p.slice(1)).join(', ')}`
      : lead;
  }, [strategyMode, rvOverallStrategy, otherStrategies, summary.entryFee]);

  /** The lifetime annuity Manual Control buys, if any — said in the sentence and the recap when it returns nothing on death. */
  const manualAnnuity = useMemo<RvInBriefAnnuity | null>(() => {
    const ownerName = (o: unknown) => ((o === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you');
    if (fundFrom === 'sell_home' && n(sellHomeDetails.purchaseAnnuity) > 0) {
      return { amount: n(sellHomeDetails.purchaseAnnuity), residualPercent: n(sellHomeDetails.rvAnnuityResidualPercent), ownerName: ownerName(sellHomeDetails.rvAnnuityOwner) };
    }
    if (otherStrategies.annuity && annuityRecord && n(annuityRecord.purchaseAmount) > 0) {
      return { amount: n(annuityRecord.purchaseAmount), residualPercent: Math.round((Number(annuityRecord.residualCapital) || 0) * 100), ownerName: ownerName(annuityRecord.owner) };
    }
    return null;
  }, [fundFrom, sellHomeDetails, otherStrategies.annuity, annuityRecord, factFind]);
  /** The nil-residual annuity the recap must name — the selected card's, or Manual Control's. */
  const nilResidualAnnuityForRecap: RvInBriefAnnuity | null = (() => {
    const a = usingSe && selectedStrategy ? annuityOfStrategy(selectedStrategy, factFind) : manualAnnuity;
    return a && a.amount > 0 && Math.round(a.residualPercent) === 0 ? a : null;
  })();
  /** "What could go wrong" about the hand-built plan in particular — its annuity's age-and-health warning and any stamp duty (22 Oct 2026). */
  const manualRiskSpecifics = () => {
    const owner = fundFrom === 'sell_home' && n(sellHomeDetails.purchaseAnnuity) > 0 ? sellHomeDetails.rvAnnuityOwner : (annuityRecord as { owner?: unknown } | null)?.owner;
    const annuityTerms = manualAnnuity ? { purchaseAmount: manualAnnuity.amount, residualPercent: manualAnnuity.residualPercent, owner } : null;
    return rvRiskSpecificsOf({ ...manualAsCandidate(), annuityTerms } as unknown as RvEvaluatedStrategy, factFind);
  };

  /**
   * The rail holds STAYING PUT — the one baseline — until Manual Control names a plan. In engine
   * mode it always does: each card carries its own figures, measured against this rail.
   */
  const railHoldsStayPut = !rvPlanSummaryName;
  const railProjections: RvProjectionRow[] = railHoldsStayPut ? stayPutProjections : projections;
  const rvRunway = useMemo(
    () => (railProjections.length ? planRunwayCopy(railProjections as never, Number(localLegacyYears) || 5) : null),
    [railProjections, localLegacyYears],
  );

  /**
   * The panel's "In brief": the SAME sentence template every engine card carries (rvInBrief.tsx),
   * fed here with Manual Control's own figures — the tiles beneath it — against staying put.
   */
  const rvInBrief = useMemo<React.ReactNode | null>(() => {
    if (!rvPlanSummaryName) return null;
    const cashBefore = initialFinancials?.cashFlow ?? 0;
    return (
      <RvInBrief
        entryFee={summary.entryFee}
        fundFrom={fundFrom}
        rentHome={rvOverallStrategy.rentHome}
        fromSaleAmount={fundFrom === 'liquid' ? null : propertyTowardEntry}
        fromSavingsAmount={fundFrom === 'liquid' ? null : liquidTargetForEntryFee}
        annuity={manualAnnuity}
        pensionBefore={initialFinancials?.socialSecurity ?? 0}
        pensionAfter={simulatedFinancials?.socialSecurity ?? 0}
        cashBefore={cashBefore}
        cashAfter={noVillageScenario ? cashBefore : (projections[0]?.netCashflow ?? cashBefore)}
        planLegacy={legacyEstimateDisplay}
        baseLegacy={manualBaselineLegacy}
        legacyYears={Number(localLegacyYears) || 0}
        shortfall={entryFeeShortfall}
      />
    );
  }, [rvPlanSummaryName, initialFinancials, simulatedFinancials, noVillageScenario, projections, legacyEstimateDisplay, manualBaselineLegacy, summary.entryFee, fundFrom, rvOverallStrategy.rentHome, localLegacyYears, entryFeeShortfall, propertyTowardEntry, liquidTargetForEntryFee, manualAnnuity]);
  const effRentHome = usingSe ? (effFundFrom === 'rent_home' || !!selectedStrategy!.inputs?.rentHome) : rvOverallStrategy.rentHome;
  const effDownsizeHome = usingSe ? (effFundFrom === 'sell_home') : rvOverallStrategy.downsizeHome;
  const effSellInvestmentProperty = usingSe ? (effFundFrom === 'investment_property') : rvOverallStrategy.sellInvestmentProperty;
  const effLegacyEstimate = usingSe
    ? (effProjections.length ? (estateOf(effProjections[localLegacyYears - 1] ?? effProjections[effProjections.length - 1]) ?? legacyEstimateDisplay) : legacyEstimateDisplay)
    : legacyEstimateDisplay;

  const rvInsightFinancials = useMemo(() => {
    const before = initialFinancials?.socialSecurity ?? 0;
    const after = noVillageScenario ? before : (effProjections[0]?.financials?.socialSecurity ?? before);
    const cashflowAfter = noVillageScenario ? (initialFinancials?.cashFlow ?? 0) : (effProjections[0]?.netCashflow ?? 0);
    let agePensionWhy = '';
    if (summary.entryFee > 0 && hasHome && (effFundFrom === 'liquid' || effFundFrom === 'rent_home' || effRentHome)) {
      // The entry contribution's treatment is decided by the Extra Allowable Amount test, and saying
      // it is "also assessed" unconditionally was wrong in the COMMON case: above the EAA the
      // resident is a homeowner and the contribution is exempt (verified against the engine — a
      // $500k entry on a kept $3.5m home assesses $3.5m, not $4.0m). Only at or below the EAA is the
      // contribution itself an assessable asset, and then the higher non-homeowner limits apply.
      agePensionWhy = summary.entryFee <= EXTRA_ALLOWABLE_AMOUNT
        ? `Your former principal home is assessable under the assets test (no 2‑year exemption in retirement village). Your entry contribution is at or below the extra allowable amount of ${formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}, so it is also counted as an asset — but the higher non-homeowner thresholds apply, and Rent Assistance may be payable on your regular fees. The change in Age Pension reflects your total assessable assets and the income test.`
        : `Your former principal home is assessable under the assets test (no 2‑year exemption in retirement village). Your entry contribution is above the extra allowable amount of ${formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}, so the contribution itself is not counted — it is treated as your home — but the lower homeowner thresholds then apply to everything else. The change in Age Pension reflects your total assessable assets and the income test.`;
    } else if (summary.entryFee > 0 && (effFundFrom === 'sell_home' || effFundFrom === 'investment_property')) {
      agePensionWhy = 'Your entry fee is funded from the sale of property. The change in Age Pension reflects your revised assessable assets and income (e.g. less home value, possible CGT, and how proceeds are invested).';
    } else if (summary.entryFee > 0) {
      agePensionWhy = 'The entry contribution is assessable under the assets test. Your Age Pension change reflects your total assessable assets and the income test.';
    }

    let cashflowDeficitNarrative = null;
    if (cashflowAfter < 0 && effProjections?.length > 0) {
      const y1 = effProjections[0];
      const drawdown = y1?.drawdownBreakdown || [];
      const firstSource = drawdown.length > 0 ? drawdown[0] : null;
      const firstLabel = firstSource?.label || '';
      const isBankOrTerm = firstLabel && !/account-based pension|abp|pension|share/i.test(firstLabel);

      if (isBankOrTerm) {
        let yearBankDepletes = null;
        let nextLabel = null;
        for (let i = 0; i < effProjections.length; i++) {
          const proj = effProjections[i];
          let totalBank = 0;
          (proj?.assets?.bankAccounts || []).forEach(acc => { totalBank += n(acc.value); });
          if (totalBank < 1000 && yearBankDepletes === null) {
            yearBankDepletes = proj!.year ?? (i + 1);
            const bd = proj!.drawdownBreakdown || [];
            for (const item of bd) {
              const norm = (item.label || '').toLowerCase();
              const amt = n(item.value);
              if (amt > 0 && (norm.includes('account-based pension') || norm.includes('abp') || norm.includes('pension'))) {
                nextLabel = item.label;
                break;
              }
              if (amt > 0 && norm.includes('share')) { nextLabel = item.label; break; }
              if (amt > 0 && (norm.includes('term') || norm.includes('deposit'))) { nextLabel = item.label; break; }
            }
            break;
          }
        }
        const bankOrFirstLabel = /bank|acc/i.test(firstLabel) && !/term deposit/i.test(firstLabel) ? 'bank account' : firstLabel;
        if (yearBankDepletes != null && nextLabel) {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}. Your ${bankOrFirstLabel} will be depleted by year ${yearBankDepletes}, at which stage the deficit will be funded from your ${nextLabel}.`;
        } else if (yearBankDepletes != null) {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}. Your ${bankOrFirstLabel} will be depleted by year ${yearBankDepletes}. After this point, the deficit will be funded from your remaining liquid assets (account-based pensions, then shares).`;
        } else {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel} throughout the projection period.`;
        }
      } else if (firstSource) {
        cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}.`;
      } else {
        const orderLabels: string[] = [];
        const types = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'];
        const typeNames: Record<string, string> = { bankAccounts: 'bank accounts', termDeposits: 'term deposits', accountBasedPensions: 'account-based pensions', shares: 'shares' };
        types.forEach(t => {
          const arr = factFind?.assets?.[t];
          if (arr?.some(a => n(a.value) > 0)) orderLabels.push(typeNames[t] || t);
        });
        cashflowDeficitNarrative = orderLabels.length > 0
          ? `This deficit will be funded from your liquid assets in the following order: ${orderLabels.join(', then ')}.`
          : `This deficit will be funded from your liquid assets (bank accounts, then account-based pensions, then shares).`;
      }
    }

    const cashflowBefore = initialFinancials?.cashFlow ?? 0; // Dashboard cashflow, before any village move
    // State-aware estate-timing note: the exit entitlement is usually only paid when the unit re-sells, and
    // each state/territory sets a different statutory backstop for when the operator must pay regardless.
    const rvStateRules = getRetirementVillageStateRules(rvState);
    const estateTimingNote = rvStateRules.code
      ? `${rvStateRules.name}: ${rvStateRules.buyback.summary}`
      : 'Exit-entitlement timeframes differ by state and territory — select your state on the Retirement Village overview to see the rule that applies to you.';
    const rentAssistance = (() => {
      const bd = effProjections[0]?.financials?.cashFlowBreakdown || [];
      const item = bd.find((r) => /rent assistance/i.test(r.label || ''));
      return item ? n(item.value) : 0;
    })();
    return { agePensionBefore: before, agePensionAfter: after, agePensionWhy, cashflowBefore, cashflowAfter, villageOngoingAnnual: summary.annualOngoing ?? 0, legacyEstimate: effLegacyEstimate, noVillageScenario, cashflowDeficitNarrative, estateTimingNote, rentAssistance };
  }, [initialFinancials, effProjections, noVillageScenario, summary.entryFee, summary.annualOngoing, hasHome, effFundFrom, effRentHome, effLegacyEstimate, factFind?.assets, rvState]);

  // Build suggestions when in Suggestion-Engine mode. We paint the loader first (60ms yield) THEN run the
  // (synchronous, multi-projection) evaluation, and hold the loader to a minimum so it reads as real work —
  // the same pattern that fixed the Aged Care engine flash.
  const entryFeeForSe = villageInputs.entryFee;

  /**
   * The just-in-time "how would new money be invested?" gate. The answer is an INPUT to the
   * modelling — invest strategies price at it — so it is asked BEFORE the engine runs, not
   * disclosed after a ranking that would reshuffle on the answer. Shown only when this run would
   * actually invest money, only until the client answers (answering saves to the profile, so it
   * is asked at most once, ever, across every module), and always skippable — a skip runs at
   * Conservative and leaves the question available next time, which is a different provenance
   * from choosing Conservative.
   */
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);
  const seRunWouldInvest = useMemo(() => {
    if (!(entryFeeForSe > 0)) return false;
    try {
      return generateRetirementVillageCandidates(factFind, villageInputs).some((c) => {
        const d = c.sellHomeDetails;
        return !!d && ((Number(d.purchaseShares) || 0) > 0 || (Number(d.downsizerContribution) || 0) > 0 || (Number(d.partnerDownsizerContribution) || 0) > 0);
      });
    } catch { return false; }
  }, [factFind, villageInputs, entryFeeForSe]);
  // The module's own questions ask this; "Not sure" there is an answer that records nothing, so once they are answered the
  // old one-question gate never shows (owner, 22 Oct 2026: it stood alone in place of the questions on a return visit).
  const needsNewMoneyAnswer = strategyMode === 'optimised' && seRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped && (factFind?.goals as Record<string, unknown> | undefined)?.rvHomeRoute == null;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) {
      setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    } else {
      // No persistence route (shouldn't happen from the app shell) — run rather than trap the user.
      setNewMoneySkipped(true);
    }
  };

  // The Transfer Balance Cap question, chained after the new-money one: only when this run would
  // COMMENCE a pension and a person's balances trip the $1.6m screen (transferBalanceCap.ts).
  // Skipping estimates cap usage from current pension balances — stated in the SOA assumptions —
  // and the engine splits any commencement at the cap either way.
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!(entryFeeForSe > 0)) return false;
    try {
      return generateRetirementVillageCandidates(factFind, villageInputs).some((c) => c.sellHomeDetails?.commenceIncomeStream === true);
    } catch { return false; }
  }, [factFind, villageInputs, entryFeeForSe]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
      : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else {
      setTbcSkipped(true);
    }
  };

  useEffect(() => {
    if (strategyMode !== 'optimised') return undefined;
    if (rvSetupOpen || rvAsking) { setSeLoading(false); return undefined; }
    if (needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    if (!(entryFeeForSe > 0)) { setRvSuggestions(null); setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: { strategies: EvaluatedStrategy[]; recommended: RecommendedStrategyEntry[] } | null = null;
      try { result = await buildRetirementVillageSuggestions(factFind, villageInputs, { legacyYears: localLegacyYears, villageEntryDate: effectiveVillageEntryDate || undefined, annuityQuote: seAnnuityQuote, setup: rvSetup }); } catch { result = null; }
      setRvSuggestions(result);
      const elapsed = Date.now() - startedAt;
      setTimeout(() => setSeLoading(false), Math.max(0, 2200 - elapsed));
    }, 60);
    return () => clearTimeout(t);
  }, [strategyMode, factFind, villageInputs, localLegacyYears, entryFeeForSe, needsNewMoneyAnswer, needsTbcAnswer, rvSetup, rvSetupOpen, rvAsking, seAnnuityQuote]);

  /**
   * "How does my plan compare?" for MANUAL control.
   *
   * The strongest protection against a client accepting a default and regretting it later is not a
   * warning, it is the alternative's number. Run on demand rather than automatically: the engine is
   * a real multi-projection evaluation, and a manual user who never asks should not pay for it on
   * every keystroke.
   */
  const [manualComparison, setManualComparison] = useState<{ loading: boolean; best: RvEvaluatedStrategy | null; ranAt: number } | null>(null);
  const runManualComparison = async () => {
    setManualComparison({ loading: true, best: null, ranAt: Date.now() });
    try {
      const result = await buildRetirementVillageSuggestions(factFind, villageInputs, { legacyYears: localLegacyYears, villageEntryDate: effectiveVillageEntryDate || undefined, setup: rvSetup });
      const movers = ((result?.strategies || []) as RvEvaluatedStrategy[]).filter((s) => s.id !== 'rv-stay-home');
      const best = movers.length
        ? movers.reduce((top, s) => ((s.snapshot?.legacyValue ?? 0) > (top.snapshot?.legacyValue ?? 0) ? s : top), movers[0]!)
        : null;
      setManualComparison({ loading: false, best, ranAt: Date.now() });
    } catch {
      setManualComparison({ loading: false, best: null, ranAt: Date.now() });
    }
  };
  // A changed plan invalidates a previous comparison — better to re-offer it than to show a stale one.
  useEffect(() => { setManualComparison(null); }, [villageInputs, entryFeeFunding, fundFrom, localLegacyYears]);

  useEffect(() => {
    if (!pendingHubAction || seLoading || !rvSuggestions) return;
    const id = selectedSeId ?? rvChosenEngineId;
    const row = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).find((x) => x.id === id);
    setPendingHubAction(null);
    if (!row) return;
    if (pendingHubAction === 'explain') { setExplainSeId(row.id ?? null); setShowRvExplanation(true); }
    else generateRvSoa(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHubAction, seLoading, rvSuggestions]);
  const rvChosen = useMemo(() => chosenRvPlanOf(savedStrategies as never), [savedStrategies]);
  /*
   * A PLAN CHOSEN BEFORE 22 OCT 2026 KEPT NO "WHEN THE MONEY RUNS OUT" LINE (owner, 23 Oct 2026: fix it). Once the
   * plans have been tested again and the chosen one is among them, its line is worked out from that plan's own rows
   * and saved with the card, so the plan page shows it from then on. Only where the card has no line yet (undefined);
   * a card that saved null (no rows when chosen) is left as it is.
   */
  useEffect(() => {
    const card = rvChosen?.inputs?.card as { runLine?: string | null; horizonYears?: number } | undefined;
    // Only a tested plan's card, from its own row: a hand-built card's line comes from its own rows when chosen.
    if (!rvChosen || !card || card.runLine !== undefined || !rvChosenEngineId || rvChosenEngineIdOf(rvChosen) !== rvChosenEngineId) return;
    const row = ((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).find((x) => x.id === rvChosenEngineId);
    if (!row?.projections?.length) return;
    const line = runwayCardLine({ rows: row.projections as never, factFind, horizonYears: Number(card.horizonYears) || Number(localLegacyYears) || 10, spentOn: 'the entry payment' });
    const chosenId = (rvChosen as { id?: unknown }).id;
    if (chosenId == null) return;
    setSavedStrategies((prev) => prev.map((x) => ((x as { id?: unknown }).id === chosenId)
      ? { ...x, inputs: { ...(x.inputs || {}), card: { ...((x.inputs as { card?: Record<string, unknown> } | undefined)?.card || {}), runLine: line } } } as typeof x
      : x));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rvChosen, rvChosenEngineId, rvSuggestions]);
  /*
   * THE CHOSEN PLAN'S FIGURES FOLLOW ITS RE-TEST (owner, 25 Oct 2026: as aged care and Support at home). When the
   * plans are tested again for the chosen unit over the same years, the chosen tested plan's figures are brought up
   * to date on its record (Home and the plan page read it), or, where the run no longer has it, it is marked out
   * of date. A plan for another unit, or over other years, is left as it is.
   */
  useEffect(() => {
    if (!rvChosen || (rvChosen as { source?: string }).source !== 'suggestion_engine' || !rvSuggestions || seLoading) return;
    const ins = (rvChosen.inputs || {}) as { unitKey?: string; engineStrategyId?: unknown; card?: { horizonYears?: number } };
    const unitNow = rvUnitKey(String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || ''), summary.entryFee);
    if (ins.unitKey !== unitNow || Number(ins.card?.horizonYears) !== (Number(localLegacyYears) || 10)) return;
    const row = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).find((x) => x.id === ins.engineStrategyId);
    const base = (rvSuggestions.stayHome as RvEvaluatedStrategy | null | undefined)?.snapshot;
    const chosenId = (rvChosen as { id?: unknown }).id;
    setSavedStrategies((prev) => {
      const next = row
        ? retestChosenRecord(prev as never, chosenId, { legacy: Number(row.snapshot?.legacyValue) || 0, pension: Number(row.snapshot?.pension) || 0, cashflow: Number(row.snapshot?.netCashflow) || 0 }, base ? { legacy: Number(base.legacyValue) || 0, pension: Number(base.pension) || 0, cashflow: Number(base.netCashflow) || 0 } : null, profileFingerprint(factFind))
        : markRecordOutOfDate(prev as never, chosenId, NOT_FOUND_ON_RETEST);
      return (next as typeof prev | null) ?? prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rvSuggestions, seLoading]);
  const rvUnitPlans = useMemo(() => rvUnitPlansOf(savedStrategies as never), [savedStrategies]);
  /*
   * From the plan page, the steps open on the CHOSEN unit (21 Oct 2026, caught live: after "Start a new model"
   * the contract on the file is empty, and "Change the contract" opened on $0). The chosen record carries
   * its contract and the card its village's name.
   */
  const restoreChosenUnit = () => {
    const ins = rvChosen?.inputs as { villageInputs?: Partial<VillageInputs>; card?: RvPlanCardRecord } | undefined;
    if (ins?.villageInputs && Number(ins.villageInputs.entryFee) > 0) setVillageInputs((prev) => ({ ...prev, ...ins.villageInputs }));
    const name = ins?.card?.village.name;
    if (name != null) setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), rvVillageName: name } }));
  };
  const openSteps = (to: AnalyserStep) => { restoreChosenUnit(); setRvView('steps'); if (to === 3) setStrategyMode('optimised'); goToStep(to); };
  /*
   * A NEW MODEL FROM SCRATCH, from the plan page (owner, 22 Oct 2026: every plan page has aged care's "Model
   * something else"). What Home's "Start a new model" does on opening (RV_START_FRESH_KEY), done in place: an
   * empty contract, the village and the answers taken off the file, step 3's questions asked again, step 1.
   * The chosen plan stays saved and chosen until another is chosen.
   */
  const startFresh = () => {
    setAnotherUnit(false);
    setVillageInputs({ ...RV_FRESH_VILLAGE_INPUTS });
    setFactFind?.((prev) => {
      const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
      for (const k of RV_FRESH_GOAL_KEYS) delete g[k];
      return { ...prev, goals: g };
    });
    setManualStartedFrom(null);
    setRvAsking(true);
    setRvView('steps');
    goToStep(1);
  };

  // Nothing is selected for the reader (aged care's rule): a card is selected by choosing it. Cleared outside engine mode.
  useEffect(() => {
    if (strategyMode !== 'optimised' && selectedSeId !== null) setSelectedSeId(null);
  }, [strategyMode, selectedSeId]);

  /**
   * "Select Strategy" on an engine card SAVES it, exactly as it does in Aged Care — the saved list
   * is what the Dashboard and the advice document read, and a selection that lived only on this
   * screen was lost on the next click. Recorded with its source and what the engine recommended.
   */
  const selectEngineStrategy = (strategy: RvEvaluatedStrategy) => {
    // Income only waits for the client's dated confirmation for this amount (guaranteed income review E5).
    if (rvChoiceHeld(strategy, factFind)) return;
    setSelectedSeId(strategy.id ?? null);
    // Already chosen: nothing to save again (it used to add a duplicate on every click).
    if (selectedSeId != null && strategy.id === selectedSeId) return;
    const id = `rv-se-${Date.now()}`;
    const recommended = rvGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    const villageName = String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || '').trim();
    const horizon = Number(localLegacyYears) || 10;
    const exitAtHorizon = exitAfter(villageInputs, horizon);
    const baseSnap = (rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined)?.snapshot;
    const sInputs = (strategy.inputs || {}) as { rentHome?: boolean; rentDetails?: { rentAmount?: number; rentExpenses?: number } };
    const card: RvPlanCardRecord = {
      plan: { legacy: Number(strategy.snapshot?.legacyValue) || 0, pension: Number(strategy.snapshot?.pension) || 0, cashflow: Number(strategy.snapshot?.netCashflow) || 0 },
      baseline: baseSnap ? { legacy: Number(baseSnap.legacyValue) || 0, pension: Number(baseSnap.pension) || 0, cashflow: Number(baseSnap.netCashflow) || 0 } : null,
      words: rvPlanWords(strategy, factFind, summary.entryFee),
      village: { name: villageName, entryFee: summary.entryFee, annualOngoing: summary.annualOngoing, fees: Number(villageInputs.ongoingWeeklyOrMonthly) || 0, feesFrequency: villageInputs.ongoingFrequency, departureFee: exitAtHorizon.dmf, backAfter: exitAtHorizon.netToResident },
      horizonYears: horizon,
      riskKinds: riskKindsFromCandidateId(String(strategy.id || '')),
      rentNet: sInputs.rentHome ? Math.max(0, (Number(sInputs.rentDetails?.rentAmount) || 0) * 52 - (Number(sInputs.rentDetails?.rentExpenses) || 0)) : 0,
      origin: 'engine',
      family: (() => {
        const compare = rvCompareFor(strategy, (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]);
        return rvFamilyRecordOf(rvGuaranteedIncomeOf(strategy, factFind), factFind, strategy.projections, compare ? { rows: compare.row.projections, note: compare.note } : null, horizon);
      })(),
      // The card's runway sentence and the plan's own risks, frozen with it for the plan page (22 Oct 2026).
      runLine: strategy.projections?.length ? runwayCardLine({ rows: strategy.projections as never, factFind, horizonYears: horizon, spentOn: 'the entry payment' }) : null,
      riskSpecifics: rvRiskSpecificsOf(strategy, factFind),
    };
    setSavedStrategies((prev) => withChosenRvPlan(prev as never, {
        id,
        name: strategy.name || 'Suggested village strategy',
        type: 'retirement_village',
        source: 'suggestion_engine',
        displayName: card.words.title,
        // The client's figures it was priced on, so Home can tell when they change (chosenPlanRefresh.ts).
        inputsFingerprint: profileFingerprint(factFind),
        inputs: {
          engineStrategyId: strategy.id ?? null,
          card,
          unitKey: rvUnitKey(villageName, summary.entryFee),
          legacyYears: horizon,
          planContext: { entryFee: summary.entryFee, annualOngoing: summary.annualOngoing, villageName },
          villageInputs: { ...villageInputs },
          ...((strategy.inputs || {}) as Record<string, unknown>),
          recommendedOptimalStrategies: recommended,
          clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
        },
        outputs: {
          summary,
          entryFee: summary.entryFee,
          annualOngoing: summary.annualOngoing,
          agePension: strategy.snapshot?.pension ?? 0,
          cashFlow: strategy.snapshot?.netCashflow ?? 0,
          legacyEstimate: strategy.snapshot?.legacyValue ?? 0,
          netWorthSchedule: buildNetWorthSchedule(strategy.projections || []),
        },
        createdAt: new Date().toISOString(),
      } as never) as never);
    setActiveSavedStrategyId(id);
  };


  if (rvView === 'hub' && rvChosen) {
    return (
      <div className="max-w-[1520px] mx-auto">
        <RvHub
          factFind={factFind}
          plan={rvChosen}
          unitCount={rvUnitPlans.length}
          entryDate={villageEntryDate || undefined}
          onChangeContract={() => openSteps(1)}
          onAnotherUnit={() => { setAnotherUnit(true); openSteps(1); }}
          onStartFresh={startFresh}
          onAllPlans={() => openSteps(3)}
          onExplain={() => { setPendingHubAction('explain'); openSteps(3); }}
          onRisks={() => setRiskStrategy({ id: String(rvChosen.inputs?.engineStrategyId || ''), name: rvChosen.inputs?.card?.words.title } as RvEvaluatedStrategy)}
          onAdvice={() => { setPendingHubAction('advice'); openSteps(3); }}
          onBuild={() => {
            restoreChosenUnit();
            // A hand-built plan reopens with every lever it was built with; a tested one seeds the ledger.
            if ((rvChosen as { source?: string }).source === 'manual') {
              reopenStrategy(rvChosen as never);
              // Its own record says where it began, if it was customised from a tested plan.
              setManualStartedFrom(((rvChosen as { customisedFrom?: string }).customisedFrom) ?? null);
            } else {
              startBlankManualPlan();
              const ins = rvChosen.inputs as Record<string, unknown> | undefined; if (ins) seedManualFromEngineInputs(ins);
              setManualStartedFrom(rvChosen.inputs?.card?.words.title ?? null);
            }
            setRvView('steps'); setStrategyMode('manual'); setShowStrategyOptions(true); goToStep(3);
          }}
          onBuildNew={() => { restoreChosenUnit(); openRvBuild(null); setRvView('steps'); goToStep(3); }}
          outOfDate={chosenPlanOutOfDate(rvChosen, factFind)}
          onGuide={onBack}
          onAssumptions={() => setActivePage('AssumptionsPage')}
          onHome={() => setActivePage('Dashboard')}
        />
        {riskStrategy && (
          <StrategyRisksPanel
            voice={rvVoice}
            strategyName={rvChosen.inputs?.card?.words.title || ''}
            onClose={() => setRiskStrategy(null)}
            /* The same specifics the tested plan's panel passes (22 Oct 2026): frozen on the card when chosen; a
               card saved before then carries none, so the saved inputs give what they can (the stamp duty). */
            risks={buildStrategyRisks(rvChosen.inputs?.card?.riskKinds || [], rvChosen.inputs?.card?.riskSpecifics
              ?? { horizonWarning: null, stampDuty: rvRiskSpecificsOf({ inputs: (rvChosen.inputs || {}) as Record<string, unknown> } as RvEvaluatedStrategy, factFind).stampDuty })}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-[1520px] mx-auto">
      <p className="text-[13px] text-slate-500">
        <button type="button" onClick={() => setActivePage('Dashboard')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button>
        {' › Retirement village › '}{step === 1 ? 'The village' : step === 2 ? 'What it means' : 'Ways to fund the move'}
      </p>
      <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="rv-title">{rvVoice.you ? 'Your retirement village' : `A retirement village for ${rvVoice.names}`}</h1>
      {step === 1 && (
        <p className="mt-1 text-[15px] text-slate-600">Three short steps: the village and its contract, what moving would mean for {rvVoice.them}, and the ways to fund the move.</p>
      )}
      <AnalyserSteps
        step={step}
        onStep={goToStep}
        steps={[
          { title: 'The village and its contract', sub: villageInputs.entryFee > 0 ? `${String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || 'The village')} · ${formatCurrency(villageInputs.entryFee)}` : 'From the disclosure statement' },
          { title: `What it means for ${rvVoice.them}`, sub: n(factFind?.home?.value) > 0 ? 'Staying put, in four numbers' : 'Renting on, in four numbers' },
          { title: 'Ways to fund the move', sub: 'Plans we tested, or build your own' },
        ]}
      />

      {step === 1 && anotherUnit && rvChosen && (
        <p className="mt-6 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="rv-another-unit-note">
          Everything is carried over from the {rvChosen.inputs?.card?.village.name || 'chosen'} plan. Change the contract figures and give this option a name; the first plan stays saved and chosen.
        </p>
      )}
      {step === 1 && (
        <div className="mt-8">
          <RvStep1
            factFind={factFind}
            setFactFind={setFactFind}
            villageInputs={villageInputs}
            setVillageInputs={setVillageInputs}
            villageEntryDate={villageEntryDate}
            setVillageEntryDate={setVillageEntryDate}
            villageEntryDateInPast={villageEntryDateInPast}
            onNext={() => goToStep(2)}
            onReadGuide={onBack}
          />
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <RvStep2
            factFind={factFind}
            reserve={rvReserve}
            todayFinancials={initialFinancials}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, Number(localLegacyYears) || 5) : null}
            legacyYears={localLegacyYears}
            villageInputs={villageInputs}
            annualOngoing={summary.annualOngoing}
            onLegacyYearsChange={setLocalLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => { setProjectionsSource('stayPut'); setShowProjectionsModal(true); }}
          />
        </div>
      )}

      {step === 3 && strategyMode !== 'manual' && (
        <div className="mt-8">
          {rvAsking ? (
            <RvQuestions
              factFind={factFind}
              entryFee={entryFeeForSe}
              firstRun={(factFind?.goals as Record<string, unknown> | undefined)?.rvHomeRoute == null}
              onRun={(answers) => {
                const add = rvAnswersToGoals(answers);
                const updated = { ...factFind, goals: { ...(factFind?.goals || {}), ...add } } as DraftFactFind;
                setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...add } }));
                setRvSetup(rvEngineSetupFromGoals(updated, rvState));
                setLocalLegacyYears(answers.years);
                setRvSetupSeen(true);
                setNewMoneySkipped(true);
                setRvAsking(false);
                setStrategyMode('optimised');
                scrollAppToTop();
              }}
              onBuildYourOwn={() => openRvBuild(null)}
              onChangeUnit={() => goToStep(1)}
              onCancel={(factFind?.goals as Record<string, unknown> | undefined)?.rvHomeRoute == null ? undefined : () => setRvAsking(false)}
            />
          ) : entryFeeForSe <= 0 ? (
            <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700">
              Enter the <strong>entry payment</strong> and the <strong>village fees</strong> on step 1, and we&apos;ll compare the ways to fund the move.
            </p>
          ) : needsNewMoneyAnswer ? (
            <Card><NewMoneyProfileGate onAnswer={answerNewMoneyProfile} onSkip={() => setNewMoneySkipped(true)} /></Card>
          ) : needsTbcAnswer ? (
            <Card>
              <TransferCapGate
                name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                onAnswer={answerTbc}
                onSkip={() => setTbcSkipped(true)}
              />
            </Card>
          ) : seLoading ? (
            <SuggestionEngineAnalysisLoader
              title="Testing the ways to fund the move"
              subtitle="We're modelling each way of paying for the unit, and staying put, over the same years."
            />
          ) : !rvSuggestions ? (
            <p className="text-[14px] text-slate-600">We couldn&apos;t test the plans for this unit. <button type="button" className="font-semibold text-brand-navy underline" onClick={() => openRvBuild(null)}>Build a plan yourself</button> instead.</p>
          ) : (
            <RvPlansView
              notices={(() => {
                const unitNow = rvUnitKey(String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || ''), summary.entryFee);
                const chosenHere = rvChosen && rvChosen.inputs?.unitKey === unitNow && ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).some((x) => x.id === (selectedSeId ?? rvChosenEngineId));
                if (chosenHere && rvChosen?.inputs?.card) {
                  return (
                    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="rv-chosen-banner">
                      <span className="text-[26px] leading-none text-emerald-500" aria-hidden>✓</span>
                      <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-slate-700">
                        <strong className="text-slate-900">{rvVoice.you ? "You've" : `${rvVoice.Names} have`} chosen &ldquo;{rvChosen.inputs.card.words.title}&rdquo;{rvChosen.inputs.card.village.name ? ` for ${rvChosen.inputs.card.village.name}` : ''}.</strong>{' '}
                        It&apos;s saved with {rvVoice.possessive} file, with the village&apos;s contract figures. The next step is the advice document — and, before signing anything, independent legal advice on the village contract.
                      </p>
                      <button type="button" onClick={() => setRvView('hub')} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="rv-go-to-plan-page">Go to the village plan</button>
                    </div>
                  );
                }
                if (rvChosen?.inputs?.card && rvChosen.inputs.unitKey !== unitNow) {
                  return (
                    <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="rv-other-unit-note">
                      Re-tested for the {formatCurrency(summary.entryFee)} unit. {rvVoice.Possessive} chosen plan is for {rvChosen.inputs.card.village.name ? `${rvChosen.inputs.card.village.name}, ` : ''}the {formatCurrency(rvChosen.inputs.card.village.entryFee)} unit, and stays chosen until {rvVoice.they} choose here.
                    </p>
                  );
                }
                return null;
              })()}
              factFind={factFind}
              strategies={(rvSuggestions.strategies || []) as RvEvaluatedStrategy[]}
              stayHome={(rvSuggestions.stayHome as RvEvaluatedStrategy | null | undefined) ?? null}
              recommended={rvRecommended}
              setAside={rvSetAside}
              villageInputs={villageInputs}
              annualOngoing={summary.annualOngoing}
              horizonYears={Number(localLegacyYears) || 10}
              ownToday={Number(initialFinancials?.netWorth) || 0}
              focus={optimisationFocus === 'all' ? 'legacy' : optimisationFocus}
              onFocus={(f) => setOptimisationFocus(f)}
              chosenId={selectedSeId ?? rvChosenEngineId}
              onChoose={(row) => selectEngineStrategy(row)}
              onIncomeOnly={incomeOnlySetter(setFactFind)}
              onExplain={(row) => { setExplainSeId(row.id ?? null); setShowRvExplanation(true); }}
              onRisks={(row) => setRiskStrategy(row)}
              renderDetail={(row) => (
                <RvStrategyCard
                  detailOnly
                  strategy={row}
                  factFind={factFind}
                  badges={[]}
                  baseline={(rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined) ?? null}
                  entryFee={summary.entryFee}
                  legacyYears={localLegacyYears}
                  clientName={factFind?.client?.firstName || 'you'}
                  partnerName={factFind?.partner?.firstName || 'your partner'}
                  isSelected={false}
                  goalNote={coveredNoteFor(row)}
                  onSelect={() => selectEngineStrategy(row)}
                  onExplain={() => { setExplainSeId(row.id ?? null); setShowRvExplanation(true); }}
                  onRisks={() => setRiskStrategy(row)}
                  alternative={alternativeAssetNote(
                    row,
                    (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[],
                    (s) => (s.snapshot as { legacyValue?: number } | undefined)?.legacyValue ?? null,
                    Number(localLegacyYears) || 5,
                  )}
                  annuityBlock={(row.annuityIncome ?? 0) > 0 ? (() => {
                    const gi = rvGuaranteedIncomeOf(row, factFind);
                    const quotedHere = !!gi && quotedIncomeFor(seAnnuityQuote, gi.annuityType, gi.residualPercent, gi.amount) != null;
                    return (
                      <div className="mt-3 rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white p-3">
                        <p className="text-[13px] font-semibold text-slate-700">Guaranteed income for life {quotedHere ? '(your quote)' : '(our estimate)'}: <span className="text-brand-navy">{formatCurrency(row.annuityIncome)} a year</span></p>
                        <EngineAnnuityTerms className="mt-2" terms={(row as { annuityTerms?: Record<string, unknown> }).annuityTerms} factFind={factFind} />
                        <AnnuityQuoteControl
                          draft={seAnnuityQuoteDraft}
                          onDraft={(next) => setSeAnnuityQuoteDraft(next === '' ? '' : Number(next))}
                          committed={seAnnuityQuote?.income ?? null}
                          scope={<>Have a quote from a provider for this guaranteed income? Enter the yearly income they offered for {gi ? formatCurrency(gi.amount) : 'this amount'}. Plans with the same type are re-compared on it, scaled to their amount; other types keep our estimate.</>}
                          onCommit={(next) => {
                            // The quote is for THIS plan's type and amount; plans of the same type use it scaled to theirs.
                            setSeAnnuityQuote(next != null && gi ? { income: next, amount: gi.amount, shape: annuityQuoteShapeOf(gi.annuityType, gi.residualPercent) } : null);
                            if (next == null) setSeAnnuityQuoteDraft('');
                          }}
                        />
                        {annuityQuoteNote(seAnnuityQuote, formatCurrency) && <p className="mt-2 text-[12px] text-slate-600" data-testid="rv-quote-note">{annuityQuoteNote(seAnnuityQuote, formatCurrency)}</p>}
                      </div>
                    );
                  })() : undefined}
                />
              )}
              onAllWays={() => setShowAllSeStrategies(true)}
              onChanges={() => { setRvAsking(true); scrollAppToTop(); }}
              onBuildYourOwn={(row) => openRvBuild(row ?? null)}
              onAdvice={() => {
                const chosen = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).find((x) => x.id === (selectedSeId ?? rvChosenEngineId));
                if (chosen) generateRvSoa(chosen);
              }}
              onChangeDecided={() => goToStep(1)}
            />
          )}
        </div>
      )}

      {step === 3 && strategyMode === 'manual' && (
        <div className="mt-8 space-y-6">
          <RvBuildPlan
            factFind={factFind}
            setFactFind={setFactFind}
            debtCard={payableDebtsOf(factFind as never).length > 0 ? (
              <DebtPayoffStrategy
                variant="section"
                heading="3 · Debts"
                factFind={factFind as never}
                debtPayments={debtPayments}
                setDebtPayments={setDebtPayments}
                debtFunding={debtFunding}
                setDebtFunding={setDebtFunding}
                repaymentBasis={debtRepaymentBasis}
                setRepaymentBasis={setDebtRepaymentBasis}
                competingUseNote="The money comes from the same assets that can fund the village entry payment, so what is spent here reduces what is left for it."
                reservedByAsset={reservedForEntryFee}
                reservedFor="the village entry payment"
              />
            ) : undefined}
            villageName={String((factFind?.goals as { rvVillageName?: string } | undefined)?.rvVillageName || '')}
            entryFee={summary.entryFee}
            reserve={rvReserve}
            horizonYears={Number(localLegacyYears) || 10}
            startedFrom={manualStartedFrom}
            onClear={() => openRvBuild(null)}
            startOptions={[
              ...rvGoalLeaders.slice(0, 3).map((l) => ({ id: String(l.id), label: rvPlanWords(l as RvEvaluatedStrategy, factFind, summary.entryFee).title })),
              { id: 'blank', label: 'A blank plan' },
            ]}
            onStartFrom={(id) => {
              if (id === 'blank') openRvBuild(null);
              else openRvBuild(((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).find((x) => String(x.id) === id) ?? null);
            }}
            route={rvOverallStrategy.downsizeHome ? 'sell' : rvOverallStrategy.rentHome ? 'rent' : 'keep'}
            onRoute={(r) => {
              setRvOverallStrategy((prev) => ({ ...prev, downsizeHome: r === 'sell', rentHome: r === 'rent', sellInvestmentProperty: r === 'sell' ? false : prev.sellInvestmentProperty }));
              // A new route starts from our draw for it: selling, the sale pays and savings stay put. And what was
              // placed starts again, so a sale's amounts are never quietly drawn from savings.
              setEntryFeeFunding({});
              setSellHomeDetails((prev) => ({ ...prev, downsizerContribution: 0, partnerDownsizerContribution: 0, commenceIncomeStream: false, giftingAmount: 0, funeralBondPurchase: 0, purchaseAnnuity: 0, purchaseInvestmentProperty: 0, investmentPropertyStampDuty: 0, purchaseShares: 0 }));
              if (r === 'rent') syncRentFromAnswers();
              noteManualChange(r === 'sell' ? 'The home is sold' : r === 'rent' ? 'The home is kept and rented out' : 'The home is kept, not rented');
            }}
            rent={manualRent}
            onRent={(patch) => {
              const next = { ...manualRent, ...patch };
              setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), rvRentPerWeek: next.weekly, rvRentCosts: next.costs, rvVacancyWeeks: next.vacancy } }));
              setRentDetails((prev) => ({ ...prev, rentAmount: rentAfterVacancy(next.weekly, next.vacancy), rentExpenses: next.costs }));
              noteManualChange(`The rent ${formatCurrency(next.weekly)} a week, ${formatCurrency(next.costs)} a year of costs, ${next.vacancy} weeks empty`);
            }}
            funding={effectiveEntryFeeFunding}
            onFunding={(key, amount) => { setEntryFeeFunding((prev) => ({ ...prev, [key]: amount })); noteManualChange(`${key.replace(/^[a-zA-Z]+-/, 'An account ')} → ${formatCurrency(amount)} towards the entry payment`); }}
            ipSoldId={rvOverallStrategy.sellInvestmentProperty ? String(rvInvestmentPropertyDetails.selectedPropertyId ?? '') : null}
            ipNetFromSale={fundFrom === 'investment_property' ? propertyTowardEntry : 0}
            onIpSell={(id) => {
              setRvOverallStrategy((prev) => ({ ...prev, sellInvestmentProperty: !!id, downsizeHome: id ? false : prev.downsizeHome }));
              if (id) setRvInvestmentPropertyDetails((prev) => ({ ...prev, selectedPropertyId: id, entryFeeAllocation: villageInputs.entryFee }));
              noteManualChange(id ? 'An investment property is sold for the entry payment' : 'The investment property is kept');
            }}
            details={sellHomeDetails as unknown as RvSaleDetails}
            onDetails={(patch, words) => { setSellHomeDetails((prev) => ({ ...prev, ...(patch as Partial<SellHomeDetails>) })); if (words) noteManualChange(words); }}
            other={{ giftingAmount: n(otherStrategies.giftingAmount), funeralBondAmount: n(otherStrategies.funeralBondAmount) }}
            onOther={(patch, words) => {
              setOtherStrategies((prev) => {
                const next = { ...prev, ...patch };
                return { ...next, gifting: n(next.giftingAmount) > 0, funeralBonds: n(next.funeralBondAmount) > 0 };
              });
              if (words) noteManualChange(words);
            }}
            working={rvCalcWorking}
            effect={{
              cashflow: projections.length ? n(projections[0]?.netCashflow) : null,
              baseCashflow: initialFinancials ? n(initialFinancials.cashFlow) : null,
              pension: simulatedFinancials ? n(simulatedFinancials.socialSecurity) : null,
              basePension: initialFinancials ? n(initialFinancials.socialSecurity) : null,
              legacy: projections.length ? legacyEstimateDisplay : null,
              baseLegacy: manualBaselineLegacy ?? null,
            }}
            lastChange={manualLastChange}
            onExplain={() => { setExplainSeId(null); setShowRvExplanation(true); }}
            onRisks={() => setRiskStrategy({ ...manualAsCandidate(), __kinds: manualRiskKinds() } as unknown as RvEvaluatedStrategy)}
            onChoose={chooseManualPlan}
            chosen={manualChosen}
            onBackToPlans={() => { setStrategyMode('optimised'); setShowStrategyOptions(false); scrollAppToTop(); }}
            reservedByAsset={reservedForDebt}
            rawFunding={entryFeeFunding}
            onYearByYear={() => { setProjectionsSource('plan'); setShowProjectionsModal(true); }}
            planRows={projections as unknown[]}
            comparison={strategyMode === 'manual' && summary.entryFee > 0 && (
              <div className="mt-3 border-t border-slate-200 pt-2 text-[13px] text-slate-700" data-testid="rv-build-compare">
                {!manualComparison ? (
                  <button type="button" onClick={runManualComparison} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Compare with the strongest option</button>
                ) : manualComparison.loading ? (
                  <span className="text-slate-500">Testing the ways to fund it…</span>
                ) : manualComparison.best ? (
                  <span>The way of moving that leaves the most, <strong>{rvPlanWords(manualComparison.best, factFind, summary.entryFee).title.replace(/^./, (c) => c.toLowerCase())}</strong>, leaves {formatCurrency(Math.round(n(manualComparison.best.snapshot?.legacyValue)))} after about {Number(localLegacyYears) || 10} years{projections.length ? `, against ${formatCurrency(Math.round(n(legacyEstimateDisplay)))} for this plan` : ''}. That doesn&apos;t make it right for you — it&apos;s a comparison, not a switch.</span>
                ) : (
                  <span className="text-slate-500">No other way of funding this unit could be tested.</span>
                )}
              </div>
            )}
          />
        </div>
      )}

      {/* Every evaluated strategy side by side — the aged care module's transparency: nothing the
          engine compared is hidden behind the goal-leader cards. Selecting here works exactly like
          selecting a leader card. */}
      {/*
        EVERY WAY TESTED, WORDED FOR THE READER (village rebuild item 8, 21 Oct 2026). The list said "All
        modelled strategies", "goal-leader cards" and "Legacy", and showed the engine's own names. Now each
        row says the plan as its card does (rvPlanWords: the accounts and amounts, in the order drawn), in
        the reader's voice, against staying put, with the words the plan cards use.
      */}
      {showAllSeStrategies && rvSuggestions && (() => {
        const rows = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]);
        const stay = rows.find((s) => s.id === 'rv-stay-home') || null;
        const pick = (s: RvEvaluatedStrategy) => allSortBy === 'cashflow' ? (s.snapshot?.netCashflow ?? 0) : allSortBy === 'pension' ? (s.snapshot?.pension ?? 0) : (s.snapshot?.legacyValue ?? 0);
        const sorted = rows.filter((s) => s.id !== 'rv-stay-home').sort((a, b) => pick(b) - pick(a));
        const diff = (n: number, base: number | null | undefined) => base == null ? null : n - base;
        const signed = (d: number | null) => d == null || Math.round(d) === 0 ? null : <span className={`block text-[12px] ${d < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{d < 0 ? '−' : '+'}{formatCurrency(Math.abs(Math.round(d)))}</span>;
        return (
          <DialogShell
            titleId="rv-all-ways-title"
            title={`Every way we tested, ${sorted.length}`}
            eyebrow={`Against staying put, over about ${localLegacyYears} years`}
            onClose={() => setShowAllSeStrategies(false)}
            size="xl"
            testId="rv-all-ways-dialog"
            flush
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
              <p className="text-[14px] text-slate-700">These are all the ways of paying for the unit we worked through for {rvVoice.them}. The plans {rvVoice.you ? 'you were shown' : `${rvVoice.names} ${rvVoice.are === 'is' ? 'was' : 'were'} shown`} are the ones that do best on {rvVoice.their} goals.</p>
              <label className="flex items-center gap-2 text-[13px] text-slate-600">Order by
                <select value={allSortBy} onChange={(e) => setAllSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[13px]">
                  <option value="legacy">Left to the family</option>
                  <option value="cashflow">Left over each year</option>
                  <option value="pension">Age Pension</option>
                </select>
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[14px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                    <th className="px-5 py-3">The plan</th>
                    <th className="px-5 py-3 text-right">Left to the family</th>
                    <th className="px-5 py-3 text-right">Age Pension, a year</th>
                    <th className="px-5 py-3 text-right">Left over, first year</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {stay && (
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td className="px-5 py-3"><p className="font-semibold text-slate-900">Staying put</p><p className="mt-0.5 text-[12.5px] text-slate-500">What every plan is measured against.</p></td>
                      <td className="px-5 py-3 text-right">{formatCurrency(stay.snapshot?.legacyValue)}</td>
                      <td className="px-5 py-3 text-right">{formatCurrency(stay.snapshot?.pension)}</td>
                      <td className="px-5 py-3 text-right">{(stay.snapshot?.netCashflow ?? 0) < 0 ? `short ${formatCurrency(-(stay.snapshot?.netCashflow ?? 0))}` : formatCurrency(stay.snapshot?.netCashflow)}</td>
                      <td className="px-5 py-3"></td>
                    </tr>
                  )}
                  {sorted.map((s) => {
                    const isSelected = s.id === selectedSeId;
                    const w = rvPlanWords(s, factFind, summary.entryFee);
                    const cash = s.snapshot?.netCashflow ?? 0;
                    return (
                      <tr key={s.id || s.name} className={`border-b border-slate-100 ${isSelected ? 'bg-blue-50/60' : ''}`}>
                        <td className="px-5 py-3">
                          <p className="font-semibold text-slate-900">{w.title}</p>
                          <p className="mt-0.5 max-w-xl text-[12.5px] text-slate-500">{w.sub}</p>
                          {(() => {
                            // Income only: the confirmation before it can be chosen, here as on the card (E5).
                            const gi = rvGuaranteedIncomeOf(s, factFind);
                            const setter = incomeOnlySetter(setFactFind);
                            return gi && gi.kind === 'incomeOnly' && setter && !isSelected
                              ? <div className="mt-2 max-w-xl"><IncomeOnlyBeforeChoose factFind={factFind} amount={gi.amount} onChange={setter} /></div>
                              : null;
                          })()}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold">{formatCurrency(s.snapshot?.legacyValue)}{signed(diff(s.snapshot?.legacyValue ?? 0, stay?.snapshot?.legacyValue))}</td>
                        <td className="px-5 py-3 text-right">{formatCurrency(s.snapshot?.pension)}{signed(diff(s.snapshot?.pension ?? 0, stay?.snapshot?.pension))}</td>
                        <td className={`px-5 py-3 text-right ${cash < 0 ? 'text-rose-700' : ''}`}>{cash < 0 ? `short ${formatCurrency(-cash)}` : formatCurrency(cash)}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-right">
                          {/* Every row can be chosen or read year by year from here — that is what the list is for. */}
                          <div className="inline-flex gap-2">
                            <Button variant={isSelected ? 'secondary' : 'brand'} size="sm" disabled={!isSelected && rvChoiceHeld(s, factFind)} onClick={() => { selectEngineStrategy(s); setShowAllSeStrategies(false); }}>
                              {isSelected ? 'Chosen' : 'Choose this plan'}
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => { setSeDeepDive({ projections: s.projections || [], name: w.title }); setShowAllSeStrategies(false); }}>
                              Year by year
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DialogShell>
        );
      })()}

      {/* The shared engine view — the same charts, monthly narrative and per-figure tooltips the Aged
          Care module gets, because these rows come from the same engine and carry the same data. The
          old RVProjectionsModal showed the table alone, which read as a different (poorer) product.
          Village fees are the recurring cost here, so the column says so. */}
      {/*
        EXPLAIN THIS, AS AGED CARE TELLS IT (owner, 22 Oct 2026: "the UI for the explain this does not match the
        updates that were carried out for aged care … all modules use consistent UI"). The old "Your plan summary"
        recap is gone from here: the plan's own title, one intro line, then the numbered lines — what is done and
        what that does to the numbers — with the money story inside "Put together", and the "=" line against
        staying put (or, decided to move, the best way to move, as the cards are). Under it, in aged care's order:
        the guaranteed income's family table, each piece in plain words, then the debts. The footer always opens
        the year-by-year figures — the tested plan's rows, or the hand-built plan's own.
      */}
      {showRvExplanation && (() => {
        const tested = usingSe && !!selectedStrategy;
        const row = tested ? (selectedStrategy as RvEvaluatedStrategy) : manualAsCandidate();
        const words = tested ? rvPlanWords(row, factFind, summary.entryFee) : manualPlanWords();
        const rows = ((tested ? selectedStrategy?.projections : projections) || []) as RvProjectionRow[];
        const horizon = Number(localLegacyYears) || 5;
        const stayRow = (rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined) ?? null;
        const decided = (factFind?.goals as { rvDecided?: string } | undefined)?.rvDecided === 'decided';
        const movers = ((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).filter((s) => s.id !== 'rv-stay-home' && !(s.inputs as Record<string, unknown> | undefined)?.stayHome);
        const bestMover = movers.length ? movers.reduce((top, s) => (n(s.snapshot?.legacyValue) > n(top.snapshot?.legacyValue) ? s : top), movers[0]!) : null;
        const stayPut = tested
          ? (stayRow?.snapshot ? { legacy: n(stayRow.snapshot.legacyValue), pension: n(stayRow.snapshot.pension), cashflow: n(stayRow.snapshot.netCashflow), investmentIncome: rvInvestmentIncomeOf(stayRow.projections as never, false) } : null)
          : (initialFinancials ? { legacy: n(manualBaselineLegacy), pension: n(initialFinancials.socialSecurity), cashflow: n(initialFinancials.cashFlow), investmentIncome: rvInvestmentIncomeOf(stayPutProjections as never, false) } : null);
        const plan = tested
          ? { legacy: n(row.snapshot?.legacyValue), pension: n(row.snapshot?.pension), cashflow: n(row.snapshot?.netCashflow), investmentIncome: rvInvestmentIncomeOf(rows as never) }
          : { legacy: n(legacyEstimateDisplay), pension: n(simulatedFinancials?.socialSecurity), cashflow: n(projections[0]?.netCashflow), investmentIncome: rvInvestmentIncomeOf(rows as never) };
        const benchmark = decided && bestMover
          ? { legacy: n(bestMover.snapshot?.legacyValue), word: 'the best way to move' }
          : stayPut ? { legacy: stayPut.legacy, word: 'staying put' } : null;
        const inputs = (row.inputs || {}) as { fundFrom?: string; rentHome?: boolean; rentDetails?: { rentAmount?: number; rentExpenses?: number }; sellHomeDetails?: Record<string, unknown> };
        const rent = tested ? inputs.rentDetails : (effRentHome ? rentDetails : null);
        const sale = (tested ? inputs.sellHomeDetails : (effDownsizeHome ? (sellHomeDetails as unknown as Record<string, unknown>) : null)) ?? null;
        const exitAtHorizon = exitAfter(villageInputs, horizon);
        const story = summariseFunding(rows as never).hasDeficit
          ? buildMoneyStory({ rows: rows as never, factFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: horizon, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the entry payment' })
          : null;
        // A guaranteed income: what the family receives if death comes early, against the same sale invested (E4).
        const gi = rvGuaranteedIncomeOf(tested ? row : { inputs: { sellHomeDetails: sale } }, factFind);
        const compare = gi && tested ? rvCompareFor(row, (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]) : null;
        const close = () => { setShowRvExplanation(false); setExplainSeId(null); };
        return (
          <PlanExplanationModal
            eyebrow="Explain this"
            title={words.title}
            intro={<>Each line says what {rvVoice.you ? 'you do' : `${rvVoice.names} do${rvVoice.s ? 'es' : ''}`}, and what that does to the numbers. &quot;Staying put&quot; means keeping the home and not moving.{decided && bestMover ? <> As {rvVoice.you ? 'you have' : `${rvVoice.names} ${rvVoice.have}`} decided to move, what the family is left is set against the best way to move.</> : null}</>}
            onClose={close}
            footer={(
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  close();
                  if (tested) setSeDeepDive({ projections: rows, name: words.title });
                  else { setProjectionsSource('plan'); setShowProjectionsModal(true); }
                }}
              >
                See the year-by-year figures
              </Button>
            )}
          >
            <RvCauseEffect
              voice={rvVoice}
              sub={words.sub}
              fundFrom={String(tested ? (inputs.fundFrom || 'liquid') : effFundFrom)}
              rentHome={effRentHome}
              hasHome={hasHome}
              homeValue={n(factFind?.home?.value)}
              entryFee={summary.entryFee}
              salePrice={n(sale?.salePrice)}
              rentWeekly={n(rent?.rentAmount)}
              rentCosts={n(rent?.rentExpenses)}
              shortfall={tested ? 0 : entryFeeShortfall}
              nilAnnuity={nilResidualAnnuityForRecap}
              villageFeesAYear={summary.annualOngoing}
              departureFee={exitAtHorizon.dmf}
              backAfter={exitAtHorizon.netToResident}
              rentAssistance={rvInsightFinancials.rentAssistance}
              plan={plan}
              stayPut={stayPut}
              benchmark={benchmark}
              horizonYears={horizon}
              moneyStory={story ? <MoneyStoryBlock story={story} testId="rv-money-story" /> : null}
              leverKinds={tested ? riskKindsFromCandidateId(String(row.id || '')) : manualRiskKinds()}
            >
              {gi && (
                <div className="mt-5" data-testid="rv-recap-family-panel">
                  <AnnuityFamilyPanel factFind={factFind} personName={gi.personName} amount={gi.amount} kind={gi.kind} incomeAYear={gi.incomeAYear} rows={rows as never} compareRows={(compare?.row.projections as never) ?? null} compareNote={compare?.note ?? null} horizonYears={horizon} testId="rv-recap-annuity-family-panel" />
                </div>
              )}
              {/* Each piece of the plan in plain words — a client should not have to know what a
                  downsizer contribution is to read the lines above (owner, 24 Sep 2026). */}
              <PlanPiecesCard
                className="mt-5"
                pieces={rvPlanPieces({
                  fundFrom: effFundFrom,
                  rentHome: effRentHome,
                  entryFee: summary.entryFee,
                  allocations: sale,
                  clientName: factFind?.client?.firstName || 'you',
                  partnerName: factFind?.partner?.firstName || 'your partner',
                  nilResidualAnnuity: !!nilResidualAnnuityForRecap,
                })}
              />
              {(() => {
                // The debts this plan keeps, and what the same plan with them paid off did ("<id>-clear-debt").
                const fig = (p: RvEvaluatedStrategy | null | undefined) => ({ estate: Number(p?.snapshot?.legacyValue) || 0, pension: Number(p?.snapshot?.pension) || 0, cashflow: Number(p?.snapshot?.netCashflow) || 0 });
                const twin = tested ? (((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).find((s) => s.id === `${row.id}-clear-debt`) ?? null) : null;
                const cleared = tested
                  ? (/-clear-debt$/.test(String(row.id)) ? clearableDebtTotal(factFind) : 0)
                  : Object.values((debtPayoffPlan?.payments || {}) as Record<string, unknown>).reduce<number>((t, x) => t + (Number(x) || 0), 0);
                return <DebtsInPlanNote factFind={factFind} clearedAmount={cleared} plan={tested ? fig(row) : null} twin={twin ? fig(twin) : null} years={horizon} context={tested ? 'tested' : 'manual'} />;
              })()}
              {/* The home kept and let: the six-year capital gains tax rule, whatever the years planned (22 Oct 2026). */}
              {effRentHome && <RentedHomeCgtNote horizonYears={Number(localLegacyYears) || 5} they={householdVoice(factFind).they} />}
              {/* Money the plan invests: the mix, and how it is usually held (23 Oct 2026). */}
              {plansInvest(words.sub) && <HowItIsInvestedNote factFind={factFind} />}
            </RvCauseEffect>
          </PlanExplanationModal>
        );
      })()}
      {/* The FY blend on row 1 is a FINANCIAL-year device, so calendar mode shows the calendar
          run's own rows — the same choice the Aged Care analyser makes. */}
      {showProjectionsModal && projectionsSource === 'stayPut' && (
        <ProjectionsModal
          onClose={() => { setShowProjectionsModal(false); setProjectionsSource('plan'); }}
          title={factFind?.home?.value ? 'Staying put — nothing changed' : 'Keep renting — nothing changed'}
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={stayPutProjections as never}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}
      {showProjectionsModal && projectionsSource === 'plan' && (
        <ProjectionsModal
          onClose={() => setShowProjectionsModal(false)}
          title={strategyMode === 'manual' ? manualPlanWords().title : undefined}
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : projectionsForModal) as never}
          onPeriodModeChange={setModalPeriodMode}
          isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}

      {riskStrategy && (
        <StrategyRisksPanel
          voice={rvVoice}
          strategyName={rvPlanWords(riskStrategy, factFind, summary.entryFee).title}
          onClose={() => setRiskStrategy(null)}
          risks={buildStrategyRisks((riskStrategy as { __kinds?: RiskKind[] }).__kinds ?? riskKindsFromCandidateId(String(riskStrategy.id || '')),
            // The hand-built plan's own annuity and duty; a tested plan's from its row (rvRiskSpecificsOf).
            riskStrategy.id === 'rv-manual' ? manualRiskSpecifics() : rvRiskSpecificsOf(riskStrategy, factFind))}
        />
      )}

      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          title={seDeepDive.name}
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={seDeepDive.projections as never}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}

      {showCompareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80">
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                    <GradientText>Strategy Comparison</GradientText>
                  </h2>
                  <p className="text-sm text-slate-600 mt-1.5">Side-by-side view of saved retirement village strategies.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCompareModal(false)}
                  className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1 px-6 py-5">
              <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100 text-slate-800">
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy name</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Entry fee</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Annual ongoing</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension (Yr1)</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Cash flow (Yr1)</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Legacy estimate</th>
                      <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rvSavedStrategies.map((strategy, index) => {
                      const outputs = strategy.outputs as {
                        entryFee?: number;
                        annualOngoing?: number;
                        agePension?: number;
                        cashFlow?: number;
                        legacyEstimate?: number;
                        legacyAtYear15?: number;
                        summary?: { entryFee?: number; annualOngoing?: number };
                      } | undefined;
                      return (
                      <tr
                        key={strategy.id}
                        className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900 align-top">{strategy.name}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.entryFee ?? outputs?.summary?.entryFee)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.annualOngoing ?? outputs?.summary?.annualOngoing)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.agePension)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${(outputs?.cashFlow ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {formatCurrency(outputs?.cashFlow)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.legacyEstimate ?? outputs?.legacyAtYear15)}</td>
                        <td className="px-4 py-3 text-center text-slate-600 align-top">
                          {new Date(strategy.createdAt || strategy.savedAt || Date.now()).toLocaleDateString()}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowCompareModal(false)}
                className="px-5 py-2.5 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export interface RetirementVillagePageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  savedStrategies: SavedStrategy[];
  setSavedStrategies: React.Dispatch<React.SetStateAction<SavedStrategy[]>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const RetirementVillagePage = ({ setActivePage, factFind, savedStrategies, setSavedStrategies, setFactFind, onSoaGenerated }: RetirementVillagePageProps) => {
  // The steps first; the guide is a link from step 1 (village review decision 2, agreed 21 Oct 2026).
  const [view, setView] = useState<'explanation' | 'calculator'>('calculator');
  /** Where the reader was when the guide opened, to put them back there. */
  const scrollWas = useRef(0);
  const appScroller = () => document.querySelector('[data-app-scroll-container]') as HTMLElement | null;
  const openGuide = () => { scrollWas.current = appScroller()?.scrollTop ?? window.scrollY; setView('explanation'); scrollAppToTop(); };
  const closeGuide = () => {
    setView('calculator');
    const y = scrollWas.current;
    requestAnimationFrame(() => { const el = appScroller(); if (el) el.scrollTop = y; else window.scrollTo(0, y); });
  };
  // The village's state is asked on step 1 and kept on the file (goals.rvState).
  const rvState = String((factFind?.goals as { rvState?: string } | undefined)?.rvState || '');
  const setRvState = (value: string) => setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), rvState: value } }));
  // The calculator stays mounted (hidden) under the guide, so the step, the answers and the plans are kept, as in
  // aged care; closing the guide puts the reader back where they were.
  return (
    <>
      {view === 'explanation' && <RetirementVillageExplained setActivePage={setActivePage} onContinue={closeGuide} rvState={rvState} setRvState={setRvState} backLabel="Back to where you were" />}
      <div className={view === 'explanation' ? 'hidden' : undefined} aria-hidden={view === 'explanation' || undefined}>
        <RetirementVillageAnalyser setActivePage={setActivePage} factFind={factFind} onBack={openGuide} savedStrategies={savedStrategies} setSavedStrategies={setSavedStrategies} rvState={rvState} setFactFind={setFactFind} onSoaGenerated={onSoaGenerated} />
      </div>
    </>
  );
};

export default RetirementVillagePage;
